import { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { pickProductMediaUrl } from '../utils/media';
import { useCmsMedia } from '../hooks/useCmsMedia';
import { ExtraMediaBrowser } from '../components/ExtraMediaBrowser';
import { calculateProductionCost, calculateRecommendedPrice, FORMULA_PARAMS, type FormulaResult } from '../../lib/pricing-formula';

/** Small info tooltip — hover the ? icon to see the hint */
function InfoTooltip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const timeout = useRef<ReturnType<typeof setTimeout>>();

  return (
    <span
      className="relative inline-flex ml-1 align-middle"
      onMouseEnter={() => { clearTimeout(timeout.current); setOpen(true); }}
      onMouseLeave={() => { timeout.current = setTimeout(() => setOpen(false), 150); }}
      onClick={(e) => { e.preventDefault(); setOpen((v) => !v); }}
    >
      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-black/20 dark:border-white/20 text-[10px] font-bold text-brand-gray dark:text-white/50 cursor-help select-none leading-none">
        ?
      </span>
      {open && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 text-xs font-normal leading-snug text-white bg-brand-dark dark:bg-white dark:text-brand-dark rounded-lg shadow-lg whitespace-normal w-56 text-center z-50 pointer-events-none">
          {text}
          <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-brand-dark dark:border-t-white" />
        </span>
      )}
    </span>
  );
}

interface CMSProduct {
  id: string;
  name?: string;
  slug?: string;
  price?: number;
  basePrice?: number;
  material?: string;
  category?: string;
  collection?: string;
  description?: string;
  'SEO-description'?: string;
  oneOfKind?: boolean;
  hasVariants?: boolean;
  stockCount?: number;
  inStock?: boolean;
  madeToOrder?: boolean;
  inventoryRecordId?: string;
  weightGrams?: number;
  materialCostPerGram?: number;
  complexity?: number;
  status?: 'draft' | 'published';
  createdAt?: string;
  variants?: Array<Record<string, any> & {
    id: string;
    stockCount?: number;
    inStock?: boolean;
    madeToOrder?: boolean;
  }>;
}

interface ProductFormState {
  name: string;
  slug: string;
  price: string;
  basePrice: string;
  material: string;
  category: string;
  collection: string;
  description: string;
  seoDescription: string;
  oneOfKind: boolean;
  hasVariants: boolean;
  weightGrams: string;
  materialCostPerGram: string;
  complexity: string;
  status: 'draft' | 'published';
  createdAt: string;
}

const emptyForm: ProductFormState = {
  name: '',
  slug: '',
  price: '',
  basePrice: '',
  material: '',
  category: '',
  collection: '',
  description: '',
  seoDescription: '',
  oneOfKind: false,
  hasVariants: false,
  weightGrams: '',
  materialCostPerGram: '',
  complexity: '',
  status: 'published',
  createdAt: '',
};

export function CMSProducts() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { getMediaByProduct, loading: mediaLoading, refreshMedia } = useCmsMedia();
  const [products, setProducts] = useState<CMSProduct[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState<ProductFormState>(emptyForm);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterCollection, setFilterCollection] = useState('all');
  const [filterMaterial, setFilterMaterial] = useState('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'draft' | 'published'>('all');
  const [filterOneOfKind, setFilterOneOfKind] = useState<'all' | 'true' | 'false'>('all');
  const [filterHasVariants, setFilterHasVariants] = useState<'all' | 'true' | 'false'>('all');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Array<{ field: string; from: string; to: string }>>([]);

  // Media upload state
  const [uploadingMedia, setUploadingMedia] = useState<{
    'product-main': boolean;
    'product-secondary': boolean;
    'product-third': boolean;
  }>({ 'product-main': false, 'product-secondary': false, 'product-third': false });
  const [uploadErrors, setUploadErrors] = useState<{
    'product-main': string | null;
    'product-secondary': string | null;
    'product-third': string | null;
  }>({ 'product-main': null, 'product-secondary': null, 'product-third': null });
  const [pendingMedia, setPendingMedia] = useState<{
    'product-main': File | null;
    'product-secondary': File | null;
    'product-third': File | null;
  }>({ 'product-main': null, 'product-secondary': null, 'product-third': null });
  const [deletingMedia, setDeletingMedia] = useState<{
    'product-main': boolean;
    'product-secondary': boolean;
    'product-third': boolean;
  }>({ 'product-main': false, 'product-secondary': false, 'product-third': false });
  const [confirmDeleteUsage, setConfirmDeleteUsage] = useState<'product-main' | 'product-secondary' | 'product-third' | null>(null);

  // Sort & view state
  const [sortBy, setSortBy] = useState<'name' | 'price-asc' | 'price-desc' | 'stock' | 'newest'>('newest');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  // Extra media browser state
  const [showExtraMediaBrowser, setShowExtraMediaBrowser] = useState(false);
  const [linkingExtraMedia, setLinkingExtraMedia] = useState(false);
  const [unlinkingExtraMedia, setUnlinkingExtraMedia] = useState<string | null>(null);
  const [recentlyUnlinkedMedia, setRecentlyUnlinkedMedia] = useState<Set<string>>(new Set());
  const [uploadingExtraMedia, setUploadingExtraMedia] = useState(false);

  // State for browsing existing media for main/secondary/third slots
  const [showMediaBrowser, setShowMediaBrowser] = useState<'product-main' | 'product-secondary' | 'product-third' | null>(null);
  const [linkingMedia, setLinkingMedia] = useState(false);

  // Variant editing state
  const [expandedVariantIndex, setExpandedVariantIndex] = useState<number | null>(null);
  const [editedVariants, setEditedVariants] = useState<Record<string, any>[]>([]);
  const [variantImageUploading, setVariantImageUploading] = useState<{ variantIndex: number; slot: number } | null>(null);
  const [variantImageError, setVariantImageError] = useState<string | null>(null);
  const [showVariantMediaBrowser, setShowVariantMediaBrowser] = useState<{ variantIndex: number; slot: number } | null>(null);

  // New product creation state
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [creatingProduct, setCreatingProduct] = useState(false);
  const [newProductForm, setNewProductForm] = useState<ProductFormState>(emptyForm);
  const [newVariants, setNewVariants] = useState<Array<{ name: string; material: string; price: string; color: string }>>([]);

  // Manual override for auto-calculated production cost
  const [manualCostOverride, setManualCostOverride] = useState(false);
  const [newManualCostOverride, setNewManualCostOverride] = useState(false);

  // Delete product state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');

  // Inline inventory management state
  const [inventoryEditing, setInventoryEditing] = useState<{
    [key: string]: {
      stockCount: number;
      saving: boolean;
      error: string | null;
      success: boolean;
    };
  }>({});
  const [madeToOrderState, setMadeToOrderState] = useState<{
    [key: string]: {
      saving: boolean;
      error: string | null;
    };
  }>({});

  const selectedProduct = useMemo(() => {
    return products.find((p) => p.id === selectedId) || null;
  }, [products, selectedId]);

  // Auto-dismiss success messages after 4s
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(null), 4000);
    return () => clearTimeout(t);
  }, [success]);

  useEffect(() => {
    const fetchProducts = async () => {
      if (!token) return;
      setLoading(true);
      setError(null);
      try {
        const res = await fetch('/api/cms/products', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || 'Failed to fetch products');
        }
        setProducts(data.products || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch products');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [token]);

  useEffect(() => {
    // Clear optimistic UI state when product changes
    setRecentlyUnlinkedMedia(new Set());

    if (!selectedProduct) {
      setForm(emptyForm);
      setEditedVariants([]);
      setExpandedVariantIndex(null);
      return;
    }

    setForm({
      name: selectedProduct.name || '',
      slug: selectedProduct.slug || '',
      price: selectedProduct.price !== undefined ? String(selectedProduct.price) : '',
      basePrice: selectedProduct.basePrice !== undefined ? String(selectedProduct.basePrice) : '',
      material: selectedProduct.material || '',
      category: selectedProduct.category || '',
      collection: selectedProduct.collection || '',
      description: selectedProduct.description || '',
      seoDescription: selectedProduct['SEO-description'] || '',
      oneOfKind: Boolean(selectedProduct.oneOfKind),
      hasVariants: Boolean(selectedProduct.hasVariants),
      weightGrams: selectedProduct.weightGrams !== undefined && selectedProduct.weightGrams !== null ? String(selectedProduct.weightGrams) : '',
      materialCostPerGram: selectedProduct.materialCostPerGram !== undefined && selectedProduct.materialCostPerGram !== null ? String(selectedProduct.materialCostPerGram) : '',
      complexity: selectedProduct.complexity !== undefined && selectedProduct.complexity !== null ? String(selectedProduct.complexity) : '',
      status: selectedProduct.status || 'published',
      createdAt: selectedProduct.createdAt ? selectedProduct.createdAt.slice(0, 10) : '',
    });

    // Initialize editable variants from selected product
    if (Array.isArray(selectedProduct.variants)) {
      setEditedVariants(selectedProduct.variants.map(v => ({ ...v })));
    } else {
      setEditedVariants([]);
    }
  }, [selectedProduct]);

  // Reset inventory editing and manual override when product changes
  useEffect(() => {
    setInventoryEditing({});
    setMadeToOrderState({});
    setManualCostOverride(false);
  }, [selectedId]);

  // Inventory management functions
  const startInventoryEditing = (itemId: string, currentStock: number) => {
    setInventoryEditing((prev) => ({
      ...prev,
      [itemId]: {
        stockCount: currentStock,
        saving: false,
        error: null,
        success: false,
      },
    }));
  };

  const cancelInventoryEditing = (itemId: string) => {
    setInventoryEditing((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  };

  const updateInventoryValue = (itemId: string, value: number) => {
    setInventoryEditing((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        stockCount: value,
      },
    }));
  };

  const saveInventoryStock = async (itemId: string, inventoryId: string) => {
    if (!token || !inventoryEditing[itemId]) return;

    const newStockCount = inventoryEditing[itemId].stockCount;

    setInventoryEditing((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], saving: true, error: null, success: false },
    }));

    try {
      const res = await fetch(`/api/cms/inventory?id=${encodeURIComponent(inventoryId)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ stockCount: newStockCount }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to update inventory');
      }

      // Update local state
      setProducts((prev) =>
        prev.map((product) => {
          if (product.id === itemId) {
            return {
              ...product,
              stockCount: newStockCount,
              inStock: newStockCount > 0,
            };
          }
          // Check if it's a variant
          if (product.variants?.some((v) => v.id === itemId)) {
            const updatedVariants = product.variants.map((v) =>
              v.id === itemId ? { ...v, stockCount: newStockCount, inStock: newStockCount > 0 } : v
            );
            const totalStock = updatedVariants.reduce((sum, v) => sum + (v.stockCount || 0), 0);
            return {
              ...product,
              variants: updatedVariants,
              stockCount: totalStock,
              inStock: totalStock > 0,
            };
          }
          return product;
        })
      );

      // Show success briefly then close
      setInventoryEditing((prev) => ({
        ...prev,
        [itemId]: { ...prev[itemId], saving: false, success: true },
      }));

      setTimeout(() => {
        cancelInventoryEditing(itemId);
      }, 1500);
    } catch (err) {
      setInventoryEditing((prev) => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          saving: false,
          error: err instanceof Error ? err.message : 'Failed to update',
        },
      }));
    }
  };

  const toggleMadeToOrder = async (itemId: string, inventoryId: string, currentValue: boolean) => {
    if (!token) return;

    setMadeToOrderState((prev) => ({
      ...prev,
      [itemId]: { saving: true, error: null },
    }));

    try {
      const res = await fetch(`/api/cms/inventory?id=${encodeURIComponent(inventoryId)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ madeToOrder: !currentValue }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to update');
      }

      // Update local state
      setProducts((prev) =>
        prev.map((product) => {
          if (product.id === itemId) {
            return { ...product, madeToOrder: !currentValue };
          }
          // Check if it's a variant
          if (product.variants?.some((v) => v.id === itemId)) {
            const updatedVariants = product.variants.map((v) =>
              v.id === itemId ? { ...v, madeToOrder: !currentValue } : v
            );
            return { ...product, variants: updatedVariants };
          }
          return product;
        })
      );

      setMadeToOrderState((prev) => ({
        ...prev,
        [itemId]: { saving: false, error: null },
      }));
    } catch (err) {
      setMadeToOrderState((prev) => ({
        ...prev,
        [itemId]: {
          saving: false,
          error: err instanceof Error ? err.message : 'Failed to update',
        },
      }));
    }
  };

  const filterOptions = useMemo(() => {
    const categories = Array.from(new Set(products.map((p) => p.category).filter(Boolean))) as string[];
    const collections = Array.from(new Set(products.map((p) => p.collection).filter(Boolean))) as string[];
    const materials = Array.from(new Set(products.map((p) => p.material).filter(Boolean))) as string[];

    return {
      categories: categories.sort((a, b) => a.localeCompare(b)),
      collections: collections.sort((a, b) => a.localeCompare(b)),
      materials: materials.sort((a, b) => a.localeCompare(b)),
    };
  }, [products]);

  const filteredProducts = useMemo(() => {
    const term = search.trim().toLowerCase();
    return products.filter((product) => {
      if (filterStatus !== 'all' && product.status !== filterStatus) {
        return false;
      }
      if (filterCategory !== 'all' && product.category !== filterCategory) {
        return false;
      }
      if (filterCollection !== 'all' && product.collection !== filterCollection) {
        return false;
      }
      if (filterMaterial !== 'all' && product.material !== filterMaterial) {
        return false;
      }
      if (filterOneOfKind !== 'all') {
        const expected = filterOneOfKind === 'true';
        if (Boolean(product.oneOfKind) !== expected) {
          return false;
        }
      }
      if (filterHasVariants !== 'all') {
        const hasVariants = Boolean(product.hasVariants) || (Array.isArray(product.variants) && product.variants.length > 0);
        const expected = filterHasVariants === 'true';
        if (hasVariants !== expected) {
          return false;
        }
      }
      if (!term) return true;
      return [product.id, product.name, product.slug]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    });
  }, [products, search, filterStatus, filterCategory, filterCollection, filterMaterial, filterOneOfKind, filterHasVariants]);

  const sortedProducts = useMemo(() => {
    const getPrice = (p: CMSProduct) => Number(p.price || p.variants?.[0]?.price || 0);
    switch (sortBy) {
      case 'price-asc': return [...filteredProducts].sort((a, b) => getPrice(a) - getPrice(b));
      case 'price-desc': return [...filteredProducts].sort((a, b) => getPrice(b) - getPrice(a));
      case 'stock': return [...filteredProducts].sort((a, b) => (b.stockCount || 0) - (a.stockCount || 0));
      case 'newest': return [...filteredProducts].sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      });
      default: return [...filteredProducts].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
  }, [filteredProducts, sortBy]);

  const stockStats = useMemo(() => {
    const totalProducts = products.length;
    const productsInStock = products.filter((product) => (product.stockCount || 0) > 0).length;
    const totalUnits = products.reduce((sum, product) => sum + (product.stockCount || 0), 0);

    return { totalProducts, productsInStock, totalUnits };
  }, [products]);

  // Formula results for all products (used by product cards)
  const formulaMap = useMemo(() => {
    const map: Record<string, FormulaResult> = {};
    for (const p of products) {
      const cost = calculateProductionCost(p.weightGrams ?? null, p.materialCostPerGram ?? null) ?? (p.basePrice ? Number(p.basePrice) : null);
      if (cost && cost > 0 && p.material) {
        map[p.id] = calculateRecommendedPrice({
          productionCost: cost,
          material: p.material,
          complexity: p.complexity ?? null,
          oneOfKind: p.oneOfKind,
        });
      }
    }
    return map;
  }, [products]);

  // Auto-computed production cost + formula recommendation for EDIT form
  const editComputedCost = useMemo(() => {
    const w = form.weightGrams ? Number(form.weightGrams) : null;
    const c = form.materialCostPerGram ? Number(form.materialCostPerGram) : null;
    return calculateProductionCost(w, c);
  }, [form.weightGrams, form.materialCostPerGram]);

  const editFormulaResult = useMemo(() => {
    const cost = editComputedCost ?? (form.basePrice ? Number(form.basePrice) : null);
    if (cost == null || !form.material) return null;
    return calculateRecommendedPrice({
      productionCost: cost,
      material: form.material,
      complexity: form.complexity ? Number(form.complexity) : null,
      oneOfKind: form.oneOfKind,
    });
  }, [editComputedCost, form.basePrice, form.material, form.complexity, form.oneOfKind]);

  // Auto-computed production cost + formula recommendation for CREATE form
  const newComputedCost = useMemo(() => {
    const w = newProductForm.weightGrams ? Number(newProductForm.weightGrams) : null;
    const c = newProductForm.materialCostPerGram ? Number(newProductForm.materialCostPerGram) : null;
    return calculateProductionCost(w, c);
  }, [newProductForm.weightGrams, newProductForm.materialCostPerGram]);

  const newFormulaResult = useMemo(() => {
    const cost = newComputedCost ?? (newProductForm.basePrice ? Number(newProductForm.basePrice) : null);
    if (cost == null || !newProductForm.material) return null;
    return calculateRecommendedPrice({
      productionCost: cost,
      material: newProductForm.material,
      complexity: newProductForm.complexity ? Number(newProductForm.complexity) : null,
      oneOfKind: newProductForm.oneOfKind,
    });
  }, [newComputedCost, newProductForm.basePrice, newProductForm.material, newProductForm.complexity, newProductForm.oneOfKind]);

  const handleSelect = (product: CMSProduct) => {
    setSelectedId(product.id);
    setSuccess(null);
    // Push to browser history so back button works
    window.history.pushState({ productId: product.id }, '', `#${product.id}`);
  };

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (event.state?.productId) {
        setSelectedId(event.state.productId);
      } else {
        setSelectedId(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const resolveImageUrl = (product: CMSProduct): string => {
    // Wait for media to load
    if (mediaLoading) return '';

    // Get media from Media table - try product id first (for linked Products field),
    // then fall back to product.id (for pattern matching)
    const productMedia = getMediaByProduct(product.id || product.id || product.slug || '');
    
    // Try to get main image from Media table
    if (productMedia.length > 0) {
      const mainImageUrl = pickProductMediaUrl(
        productMedia,
        ['product-main', '-main', 'main'],
        'product-main'
      );
      if (mainImageUrl) return mainImageUrl;
    }

    // Fallback to variant images
    if (product.variants && product.variants.length > 0) {
      const firstVariant = product.variants[0];
      if (firstVariant.images) {
        const images = Array.isArray(firstVariant.images)
          ? firstVariant.images
          : Object.values(firstVariant.images);
        if (images.length > 0 && images[0]) {
          return images[0];
        }
      }
    }

    // No image available
    return '';
  };

  const handleFieldChange = (key: keyof ProductFormState, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const getChanges = () => {
    if (!selectedProduct) return [] as Array<{ field: string; from: string; to: string }>;

    const original = {
      name: selectedProduct.name || '',
      slug: selectedProduct.slug || '',
      price: selectedProduct.price !== undefined ? String(selectedProduct.price) : '',
      basePrice: selectedProduct.basePrice !== undefined ? String(selectedProduct.basePrice) : '',
      material: selectedProduct.material || '',
      category: selectedProduct.category || '',
      collection: selectedProduct.collection || '',
      description: selectedProduct.description || '',
      seoDescription: selectedProduct['SEO-description'] || '',
      oneOfKind: Boolean(selectedProduct.oneOfKind),
      weightGrams: selectedProduct.weightGrams !== undefined && selectedProduct.weightGrams !== null ? String(selectedProduct.weightGrams) : '',
      materialCostPerGram: selectedProduct.materialCostPerGram !== undefined && selectedProduct.materialCostPerGram !== null ? String(selectedProduct.materialCostPerGram) : '',
      complexity: selectedProduct.complexity !== undefined && selectedProduct.complexity !== null ? String(selectedProduct.complexity) : '',
      status: selectedProduct.status || 'published',
      createdAt: selectedProduct.createdAt ? selectedProduct.createdAt.slice(0, 10) : '',
    };

    const next = {
      name: form.name,
      slug: form.slug,
      price: form.price,
      basePrice: form.basePrice,
      material: form.material,
      category: form.category,
      collection: form.collection,
      description: form.description,
      seoDescription: form.seoDescription,
      oneOfKind: form.oneOfKind,
      weightGrams: form.weightGrams,
      materialCostPerGram: form.materialCostPerGram,
      complexity: form.complexity,
      status: form.status,
      createdAt: form.createdAt,
    };

    const changes: Array<{ field: string; from: string; to: string }> = [];

    Object.keys(next).forEach((key) => {
      const fromValue = String((original as any)[key] ?? '');
      const toValue = String((next as any)[key] ?? '');
      if (fromValue !== toValue) {
        changes.push({ field: key, from: fromValue || '—', to: toValue || '—' });
      }
    });

    // Check for variant changes
    if (variantsModified) {
      changes.push({
        field: 'variants',
        from: `${selectedProduct.variants?.length || 0} variants`,
        to: `${editedVariants.length} variants (modified)`,
      });
    }

    return changes;
  };

  const handleSave = async () => {
    if (!selectedProduct || !token) return;

    const changes = getChanges();
    if (changes.length === 0 && !variantsModified) {
      setSuccess('No product field changes to save. Media uploads are saved automatically.');
      return;
    }

    setPendingChanges(changes);
    setShowConfirm(true);
  };

  const handleConfirmSave = async () => {
    if (!selectedProduct || !token) return;
    setSaving(true);
    setError(null);
    setSuccess(null);

    const fields: Record<string, any> = {
      name: form.name,
      slug: form.slug,
      description: form.description,
      'SEO-description': form.seoDescription,
      material: form.material,
      category: form.category,
      collection: form.collection,
      oneOfKind: form.oneOfKind,
      status: form.status,
    };

    if (form.price !== '') fields.price = Number(form.price);
    // Send computed basePrice unless manual override is active
    if (manualCostOverride) {
      if (form.basePrice !== '') fields.basePrice = Number(form.basePrice);
    } else if (editComputedCost !== null) {
      fields.basePrice = editComputedCost;
    } else if (form.basePrice !== '') {
      fields.basePrice = Number(form.basePrice);
    }
    if (form.weightGrams !== '') fields.weightGrams = Number(form.weightGrams);
    if (form.materialCostPerGram !== '') fields.materialCostPerGram = Number(form.materialCostPerGram);
    if (form.complexity !== '') fields.complexity = Number(form.complexity);
    if (form.createdAt !== '') fields.createdAt = form.createdAt;

    // Include variants if modified
    if (variantsModified && editedVariants.length > 0) {
      fields.variants = JSON.stringify(editedVariants);
    }

    // Log the payload being sent for debugging
    console.log('[CMS Save] Sending update for product:', selectedProduct.id);
    console.log('[CMS Save] Fields being sent:', JSON.stringify(fields, null, 2));

    try {
      const res = await fetch('/api/cms/products', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          recordId: selectedProduct.id,
          fields,
        }),
      });

      const data = await res.json();

      // Log detailed error info for debugging
      if (!res.ok) {
        console.error('[CMS Save] Error response:', {
          status: res.status,
          statusText: res.statusText,
          data: data,
          sentFields: fields,
          recordId: selectedProduct.id,
        });

        // Build a more descriptive error message
        let errorMsg = `Error ${res.status}: `;
        if (data.details) {
          // Try to parse Airtable error details
          try {
            const airtableError = typeof data.details === 'string' ? JSON.parse(data.details) : data.details;
            errorMsg += airtableError.error?.message || airtableError.message || data.details;
          } catch {
            errorMsg += data.details;
          }
        } else {
          errorMsg += data.error || 'Failed to update product';
        }

        throw new Error(errorMsg);
      }

      console.log('[CMS Save] Success:', data);
      const updatedFields = data?.product?.fields || {};

      // Parse variants if they were returned as a string
      if (typeof updatedFields.variants === 'string') {
        try {
          updatedFields.variants = JSON.parse(updatedFields.variants);
        } catch (e) {
          console.warn('Failed to parse variants from response');
        }
      }

      setProducts((prev) =>
        prev.map((product) =>
          product.id === selectedProduct.id
            ? { ...product, ...updatedFields }
            : product
        )
      );

      // Update editedVariants to match saved state (clears "Modified" indicator)
      if (updatedFields.variants) {
        setEditedVariants(updatedFields.variants.map((v: any) => ({ ...v })));
      }

      setSuccess('Product updated successfully');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to update product';
      console.error('[CMS Save] Caught error:', errorMsg);
      setError(errorMsg);
    } finally {
      setSaving(false);
      setShowConfirm(false);
    }
  };

  /**
   * Shared media upload — all upload paths (main slots, extra, variant) use this.
   */
  const uploadMediaToProduct = async (opts: {
    file: File;
    usage: string;
    mediaId: string;
    onUploading: (v: boolean) => void;
    onSuccess?: (data: any) => void;
    onError?: (msg: string) => void;
    successMessage: string;
  }) => {
    if (!selectedProduct || !token) return;

    const mediaType = opts.file.type.startsWith('video/') ? 'video' : 'image';
    const maxSize = mediaType === 'video' ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
    if (opts.file.size > maxSize) {
      const msg = `File too large. Max ${mediaType === 'video' ? '100MB' : '10MB'}.`;
      if (opts.onError) opts.onError(msg);
      else setError(msg);
      return;
    }

    opts.onUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', opts.file);
      formData.append('productId', selectedProduct.id);
      formData.append('productName', selectedProduct.name || '');
      formData.append('productCategory', selectedProduct.category || '');
      formData.append('mediaType', mediaType);
      formData.append('usage', opts.usage);
      formData.append('existingMediaId', opts.mediaId);
      formData.append('productAirtableRecordId', selectedProduct.id);

      const response = await fetch('/api/cms/media', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.details || data.error || 'Upload failed');
      }

      opts.onSuccess?.(data);
      setSuccess(opts.successMessage);
      await refreshMedia();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Upload failed';
      if (opts.onError) opts.onError(errorMsg);
      setError(errorMsg);
    } finally {
      opts.onUploading(false);
    }
  };

  /**
   * Upload to main/secondary/third slot
   */
  const handleMediaUpload = async (
    file: File,
    usage: 'product-main' | 'product-secondary' | 'product-third',
    existingMediaId?: string
  ) => {
    const mediaId = existingMediaId || `${selectedProduct?.id}-${usage}`;
    const mediaType = file.type.startsWith('video/') ? 'video' : 'image';
    await uploadMediaToProduct({
      file,
      usage,
      mediaId,
      onUploading: (v) => setUploadingMedia(prev => ({ ...prev, [usage]: v })),
      onError: (msg) => setUploadErrors(prev => ({ ...prev, [usage]: msg })),
      onSuccess: () => setPendingMedia(prev => ({ ...prev, [usage]: null })),
      successMessage: `${usage.replace('product-', '')} ${mediaType} uploaded successfully in staging`,
    });
  };

  /**
   * Handle media unlinking (removes product linkage without deleting from Cloudinary)
   */
  const handleMediaDelete = async (
    usage: 'product-main' | 'product-secondary' | 'product-third',
    mediaId: string,
    cloudinaryPublicId: string
  ) => {
    if (!selectedProduct || !token) return;

    setDeletingMedia(prev => ({ ...prev, [usage]: true }));
    setError(null);
    setSuccess(null);

    try {
      // Use PATCH with 'unlink' action instead of DELETE
      // This removes the product linkage but keeps the media in Cloudinary
      const response = await fetch('/api/cms/media', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'unlink',
          mediaId,
          productAirtableRecordId: selectedProduct.id,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.details || data.error || 'Unlink failed');
      }

      setSuccess(`${usage.replace('product-', '')} media unlinked successfully`);
      setConfirmDeleteUsage(null);

      // Refresh MediaContext to remove the deleted image immediately
      await refreshMedia();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Delete failed';
      setError(errorMsg);
    } finally {
      setDeletingMedia(prev => ({ ...prev, [usage]: false }));
    }
  };

  /**
   * Handle linking existing media to main/secondary/third slots
   */
  const handleLinkMedia = async (mediaId: string, _recordId: string, _cloudinaryUrl: string) => {
    if (!selectedProduct || !token || !showMediaBrowser) return;

    setLinkingMedia(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch('/api/cms/media', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'link',
          mediaId,
          productAirtableRecordId: selectedProduct.id,
          productId: selectedProduct.id, // Used to generate new media record ID
          newUsage: showMediaBrowser, // Creates new record with this usage
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.details || data.error || 'Link failed');
      }

      const usageLabel = showMediaBrowser.replace('product-', '');
      if (data.alreadyLinked) {
        setSuccess(`${usageLabel} media is already linked to this product`);
      } else if (data.created) {
        setSuccess(`New ${usageLabel} image set from library`);
      } else if (data.replaced) {
        setSuccess(`${usageLabel} image updated from library`);
      } else {
        setSuccess(`${usageLabel} media linked successfully`);
      }

      setShowMediaBrowser(null);

      // Refresh MediaContext to show the newly linked media
      await refreshMedia();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to link media';
      setError(errorMsg);
    } finally {
      setLinkingMedia(false);
    }
  };

  /**
   * Handle linking existing extra media to current product
   */
  const handleLinkExtraMedia = async (mediaId: string, _recordId: string, _cloudinaryUrl: string) => {
    if (!selectedProduct || !token) return;

    setLinkingExtraMedia(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch('/api/cms/media', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'link',
          mediaId,
          productAirtableRecordId: selectedProduct.id,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.details || data.error || 'Link failed');
      }

      if (data.alreadyLinked) {
        setSuccess('Media is already linked to this product');
      } else {
        setSuccess('Extra media linked successfully');
      }

      setShowExtraMediaBrowser(false);

      // Refresh MediaContext to show the newly linked media
      await refreshMedia();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to link media';
      setError(errorMsg);
    } finally {
      setLinkingExtraMedia(false);
    }
  };

  /**
   * Handle unlinking extra media from current product
   */
  const handleUnlinkExtraMedia = async (mediaId: string) => {
    if (!selectedProduct || !token) return;

    setUnlinkingExtraMedia(mediaId);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch('/api/cms/media', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'unlink',
          mediaId,
          productAirtableRecordId: selectedProduct.id,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.details || data.error || 'Unlink failed');
      }

      setSuccess('Extra media unlinked successfully');

      // Optimistically hide the unlinked media immediately
      setRecentlyUnlinkedMedia(prev => new Set(prev).add(mediaId));

      // Refresh MediaContext to sync with server
      await refreshMedia();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to unlink media';
      setError(errorMsg);
    } finally {
      setUnlinkingExtraMedia(null);
    }
  };

  /**
   * Handle uploading a new extra media file for the current product
   */
  /**
   * Upload extra media for current product
   */
  const handleExtraMediaUpload = async (file: File) => {
    const timestamp = Date.now().toString(36);
    const mediaType = file.type.startsWith('video/') ? 'video' : 'image';
    await uploadMediaToProduct({
      file,
      usage: 'product-extra',
      mediaId: `${selectedProduct?.id}-product-extra-${timestamp}`,
      onUploading: (v) => setUploadingExtraMedia(v),
      successMessage: `Extra ${mediaType} uploaded successfully`,
    });
  };

  /**
   * Handle variant field change (price, material, etc.)
   */
  const handleVariantFieldChange = (variantIndex: number, field: string, value: string | number) => {
    setEditedVariants(prev => {
      const updated = [...prev];
      updated[variantIndex] = { ...updated[variantIndex], [field]: value };
      return updated;
    });
  };

  /**
   * Upload variant media (image or video)
   */
  const handleVariantImageUpload = async (file: File, variantIndex: number, imageSlot: number) => {
    const variantId = editedVariants[variantIndex]?.id || `variant-${variantIndex}`;
    const variantMediaId = `${selectedProduct?.id}-variant-${variantId}-img${imageSlot}`;
    await uploadMediaToProduct({
      file,
      usage: 'variant-image',
      mediaId: variantMediaId,
      onUploading: (v) => {
        if (v) setVariantImageUploading({ variantIndex, slot: imageSlot });
        else setVariantImageUploading(null);
      },
      onError: (msg) => setVariantImageError(msg),
      onSuccess: (data) => {
        setEditedVariants(prev => {
          const updated = [...prev];
          const variant = { ...updated[variantIndex] };
          const images = Array.isArray(variant.images)
            ? [...variant.images]
            : variant.images ? Object.values(variant.images) : [];
          while (images.length <= imageSlot) images.push('');
          images[imageSlot] = data.url || data.cloudinaryUrl;
          variant.images = images;
          updated[variantIndex] = variant;
          return updated;
        });
      },
      successMessage: `Variant image ${imageSlot + 1} uploaded successfully`,
    });
  };

  /**
   * Handle selecting existing media from browser for variant image
   */
  const handleVariantMediaSelect = (_mediaId: string, _recordId: string, cloudinaryUrl: string) => {
    if (!showVariantMediaBrowser) return;

    const { variantIndex, slot } = showVariantMediaBrowser;

    // Update variant with the selected image URL
    setEditedVariants(prev => {
      const updated = [...prev];
      const variant = { ...updated[variantIndex] };
      const images = Array.isArray(variant.images)
        ? [...variant.images]
        : variant.images
          ? Object.values(variant.images)
          : [];
      // Pad with empty strings if needed
      while (images.length <= slot) {
        images.push('');
      }
      images[slot] = cloudinaryUrl;
      variant.images = images;
      updated[variantIndex] = variant;
      return updated;
    });

    setShowVariantMediaBrowser(null);
    setSuccess(`Variant image ${slot + 1} selected from library`);
  };

  /**
   * Check if variants have been modified
   */
  const variantsModified = useMemo(() => {
    if (!selectedProduct?.variants || !editedVariants.length) return false;
    return JSON.stringify(selectedProduct.variants) !== JSON.stringify(editedVariants);
  }, [selectedProduct?.variants, editedVariants]);

  /**
   * Handle new product form field change
   */
  const handleNewProductFieldChange = (key: keyof ProductFormState, value: string | boolean) => {
    setNewProductForm((prev) => ({ ...prev, [key]: value }));
  };

  /**
   * Add a new variant to the new product
   */
  const handleAddVariant = () => {
    setNewVariants((prev) => [...prev, { name: '', material: '', price: '', color: '' }]);
  };

  /**
   * Remove a variant from the new product
   */
  const handleRemoveVariant = (index: number) => {
    setNewVariants((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Update a variant field in the new product
   */
  const handleNewVariantFieldChange = (index: number, field: string, value: string) => {
    setNewVariants((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  /**
   * Create a new product
   */
  const handleCreateProduct = async () => {
    if (!token) return;

    // Validate required fields
    const errors: string[] = [];
    if (!newProductForm.name.trim()) errors.push('Name is required');
    if (!newProductForm.slug.trim()) errors.push('Slug is required');
    if (!newProductForm.price.trim() || isNaN(Number(newProductForm.price))) errors.push('Valid price is required');
    const hasComputedCost = newComputedCost !== null;
    const hasManualCost = newProductForm.basePrice.trim() !== '' && !isNaN(Number(newProductForm.basePrice));
    if (!hasComputedCost && !hasManualCost) errors.push('Production cost is required (provide manually or fill weight + cost/gram)');
    if (!newProductForm.material.trim()) errors.push('Material is required');
    if (!newProductForm.category.trim()) errors.push('Category is required');
    if (!newProductForm.collection.trim()) errors.push('Collection is required');
    if (!newProductForm.description.trim()) errors.push('Description is required');
    if (newProductForm.description.length > 5000) errors.push('Description exceeds 5000 character limit');
    if (!newProductForm.seoDescription.trim()) errors.push('SEO description is required');
    if (newProductForm.seoDescription.length > 1000) errors.push('SEO description exceeds 1000 character limit');

    // Validate variants if any
    if (newVariants.length > 0) {
      newVariants.forEach((v, i) => {
        if (!v.name.trim()) errors.push(`Variant ${i + 1}: Name is required`);
      });
    }

    if (errors.length > 0) {
      setError(errors.join(', '));
      return;
    }

    setCreatingProduct(true);
    setError(null);
    setSuccess(null);

    try {
      // Use computed cost unless manual override, fall back to manual
      const finalBasePrice = (!newManualCostOverride && newComputedCost !== null)
        ? newComputedCost
        : Number(newProductForm.basePrice);

      const payload: Record<string, any> = {
        name: newProductForm.name.trim(),
        slug: newProductForm.slug.trim(),
        price: Number(newProductForm.price),
        basePrice: finalBasePrice,
        material: newProductForm.material.trim(),
        category: newProductForm.category.trim(),
        collection: newProductForm.collection.trim(),
        description: newProductForm.description.trim(),
        seoDescription: newProductForm.seoDescription.trim(),
        oneOfKind: newProductForm.oneOfKind,
        status: newProductForm.status,
      };

      if (newProductForm.weightGrams !== '') payload.weightGrams = Number(newProductForm.weightGrams);
      if (newProductForm.materialCostPerGram !== '') payload.materialCostPerGram = Number(newProductForm.materialCostPerGram);
      if (newProductForm.complexity !== '') payload.complexity = Number(newProductForm.complexity);

      if (newVariants.length > 0) {
        payload.variants = newVariants
          .filter((v) => v.name.trim())
          .map((v) => ({
            name: v.name.trim(),
            material: v.material.trim() || undefined,
            price: v.price.trim() ? Number(v.price) : undefined,
            color: v.color.trim() || undefined,
          }));
      }

      console.log('[CMS Create] Sending payload:', payload);

      const res = await fetch('/api/cms/products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        console.error('[CMS Create] Error:', data);
        throw new Error(data.details?.join(', ') || data.error || 'Failed to create product');
      }

      console.log('[CMS Create] Success:', data);

      // Add the new product to the list
      const newProduct: CMSProduct = {
        id: data.product.id,
        name: data.product.name,
        slug: data.product.slug,
        price: data.product.price,
        basePrice: data.product.basePrice,
        material: data.product.material,
        category: data.product.category,
        collection: data.product.collection,
        description: data.product.description,
        'SEO-description': data.product['SEO-description'],
        oneOfKind: data.product.oneOfKind,
        hasVariants: data.product.hasVariants,
        stockCount: 0,
        inStock: false,
        status: data.product.status || 'published',
        variants: Array.isArray(data.product.variants) ? data.product.variants : (data.product.variants ? JSON.parse(data.product.variants) : []),
      };

      setProducts((prev) => [newProduct, ...prev]);
      setSuccess('Product created successfully! Now add media to complete setup.');

      // Reset form and switch to edit mode for the new product
      setNewProductForm(emptyForm);
      setNewVariants([]);
      setIsCreatingNew(false);

      // Select the new product for editing (to add media)
      setSelectedId(newProduct.id);
      window.history.pushState({ productId: newProduct.id }, '', `#${newProduct.id}`);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to create product';
      console.error('[CMS Create] Caught error:', errorMsg);
      setError(errorMsg);
    } finally {
      setCreatingProduct(false);
    }
  };

  /**
   * Cancel creating new product
   */
  const handleCancelCreate = () => {
    setIsCreatingNew(false);
    setNewProductForm(emptyForm);
    setNewVariants([]);
    setError(null);
    setSuccess(null);
  };

  /**
   * Delete a product
   */
  const handleDeleteProduct = async () => {
    if (!selectedProduct || !token) return;

    setDeletingProduct(true);
    setError(null);

    try {
      const res = await fetch('/api/cms/products', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ recordId: selectedProduct.id }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.details || data.error || 'Failed to delete product');
      }

      console.log('[CMS Delete] Product deleted:', data);

      // Remove from local state
      setProducts((prev) => prev.filter((p) => p.id !== selectedProduct.id));

      // Close modal and go back to list
      setShowDeleteConfirm(false);
      setSelectedId(null);
      window.history.pushState({}, '', window.location.pathname);

      setSuccess('Product deleted successfully');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to delete product';
      console.error('[CMS Delete] Error:', errorMsg);
      setError(errorMsg);
    } finally {
      setDeletingProduct(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-sand dark:bg-brand-dark">
      <header className="bg-white/80 dark:bg-brand-charcoal/60 backdrop-blur-md shadow-sm border-b border-black/5 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-4 mb-2">
              <button
                onClick={() => navigate('/cms/dashboard')}
                className="text-sm text-brand-gray hover:text-brand-dark/80 dark:text-white/60 dark:hover:text-white/90"
              >
                ← Dashboard
              </button>
              <span className="text-black/20 dark:text-white/20">|</span>
              <a
                href="/"
                className="text-sm text-brand-gray hover:text-brand-dark/80 dark:text-white/60 dark:hover:text-white/90"
              >
                View Store
              </a>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-brand-dark dark:text-white">Products</h1>
            <p className="text-brand-gray dark:text-white/60 mt-1">
              {isCreatingNew ? 'Create a new product' : 'Edit product details and pricing'}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <a
              href="https://camila-parente-staging-store.vercel.app/shop"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-brand-blue hover:bg-brand-dark text-white rounded-lg text-sm font-medium transition-colors"
            >
              View Staging Store
            </a>
            <button
              onClick={() => navigate('/cms/inventory')}
              className="px-4 py-2 border border-black/15 dark:border-white/15 text-brand-dark/80 dark:text-white/80 rounded-lg text-sm font-medium hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
            >
              Edit Inventory
            </button>
            {!selectedProduct && !isCreatingNew && (
              <button
                onClick={() => setIsCreatingNew(true)}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Product
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 space-y-6">
        {/* New Product Creation Form */}
        {isCreatingNew && (
          <section className="bg-white/80 dark:bg-brand-charcoal/60 backdrop-blur-md rounded-xl shadow-sm border border-black/5 dark:border-white/10 p-6">
            <div className="space-y-6">
              {/* Back Button */}
              <button
                onClick={handleCancelCreate}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 text-brand-dark/80 dark:text-white/90 rounded-lg transition-all group border border-black/5 dark:border-white/10"
              >
                <svg className="w-5 h-5 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span className="font-semibold">Cancel</span>
              </button>

              <div className="flex flex-col gap-1">
                <h2 className="text-2xl font-semibold text-brand-dark dark:text-white">Create New Product</h2>
                <p className="text-sm text-brand-gray dark:text-white/60">
                  Fill in the product details below. After creation, you'll be redirected to add media.
                </p>
              </div>

              {/* Toast notification rendered at bottom of viewport */}

              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                  Name *
                  <input
                    value={newProductForm.name}
                    onChange={(e) => handleNewProductFieldChange('name', e.target.value)}
                    placeholder="e.g., Golden Lotus Earrings"
                    className="mt-1 w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
                  />
                </label>
                <label className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                  Slug * (URL-friendly)
                  <input
                    value={newProductForm.slug}
                    onChange={(e) => handleNewProductFieldChange('slug', e.target.value)}
                    placeholder="e.g., golden-lotus-earrings"
                    className="mt-1 w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
                  />
                </label>
                <div className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                  Price * (€)
                  <input
                    type="number"
                    value={newProductForm.price}
                    onChange={(e) => handleNewProductFieldChange('price', e.target.value)}
                    placeholder="e.g., 150"
                    className="mt-1 w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
                  />
                  {newFormulaResult && (
                    <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-brand-gray dark:text-white/50">
                        Formula: €{newFormulaResult.recommendedPrice}
                      </span>
                      {(() => {
                        const p = Number(newProductForm.price);
                        const f = newFormulaResult.recommendedPrice;
                        const diff = f > 0 ? Math.round(((p - f) / f) * 100) : null;
                        return newProductForm.price && diff !== null && isFinite(diff) ? (
                          <span className={`text-xs font-medium ${
                            p >= f ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'
                          }`}>
                            {p >= f ? '+' : ''}{diff}% vs formula
                          </span>
                        ) : null;
                      })()}
                      <button
                        type="button"
                        onClick={() => handleNewProductFieldChange('price', String(newFormulaResult!.recommendedPrice))}
                        className="text-xs text-brand-blue hover:underline"
                      >
                        Apply
                      </button>
                    </div>
                  )}
                </div>
                <div className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                  Production Cost{!newComputedCost ? ' *' : ''} (€) <InfoTooltip text="Auto-calculated from weight × cost/gram when both are filled. You can override manually for edge cases." />
                  {newComputedCost !== null && !newManualCostOverride ? (
                    <>
                      <div className="mt-1 w-full px-3 py-2 bg-gray-50 dark:bg-brand-charcoal/40 border border-black/10 dark:border-white/10 rounded-lg text-brand-dark dark:text-white">
                        €{newComputedCost.toFixed(2)} <span className="text-xs text-brand-gray dark:text-white/50">(auto)</span>
                      </div>
                      <button type="button" onClick={() => setNewManualCostOverride(true)} className="mt-1 text-xs text-brand-blue hover:underline">Override</button>
                    </>
                  ) : (
                    <>
                      <input
                        type="number"
                        value={newProductForm.basePrice}
                        onChange={(e) => handleNewProductFieldChange('basePrice', e.target.value)}
                        placeholder="e.g., 45"
                        className="mt-1 w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
                      />
                      {newManualCostOverride && newComputedCost !== null && (
                        <button type="button" onClick={() => setNewManualCostOverride(false)} className="mt-1 text-xs text-brand-blue hover:underline">Use auto (€{newComputedCost.toFixed(2)})</button>
                      )}
                    </>
                  )}
                </div>
                <label className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                  Weight (grams)
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newProductForm.weightGrams}
                    onChange={(e) => handleNewProductFieldChange('weightGrams', e.target.value)}
                    placeholder="e.g. 12.5"
                    className="mt-1 w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
                  />
                </label>
                <label className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                  Material Cost/gram (€)
                  <input
                    type="number"
                    step="0.0001"
                    min="0"
                    value={newProductForm.materialCostPerGram}
                    onChange={(e) => handleNewProductFieldChange('materialCostPerGram', e.target.value)}
                    placeholder="e.g. 0.0650"
                    className="mt-1 w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
                  />
                </label>
                <label className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                  Complexity (1–10) <InfoTooltip text="How intricate is this piece to make? 1 = simple, 10 = extremely detailed. Higher complexity means more labor and should command a higher price." />
                  <input
                    type="number"
                    min="1"
                    max="10"
                    step="1"
                    value={newProductForm.complexity}
                    onChange={(e) => handleNewProductFieldChange('complexity', e.target.value)}
                    placeholder="1–10"
                    className="mt-1 w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
                  />
                </label>
                <label className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                  Material *
                  <input
                    value={newProductForm.material}
                    onChange={(e) => handleNewProductFieldChange('material', e.target.value)}
                    placeholder="e.g., 18k Gold Plated Brass"
                    className="mt-1 w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
                  />
                </label>
                <label className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                  Category *
                  <input
                    value={newProductForm.category}
                    onChange={(e) => handleNewProductFieldChange('category', e.target.value)}
                    placeholder="e.g., Earrings"
                    list="category-suggestions"
                    className="mt-1 w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
                  />
                  <datalist id="category-suggestions">
                    {filterOptions.categories.map((cat) => (
                      <option key={cat} value={cat} />
                    ))}
                  </datalist>
                </label>
                <label className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                  Collection *
                  <input
                    value={newProductForm.collection}
                    onChange={(e) => handleNewProductFieldChange('collection', e.target.value)}
                    placeholder="e.g., Lotus Collection"
                    list="collection-suggestions"
                    className="mt-1 w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
                  />
                  <datalist id="collection-suggestions">
                    {filterOptions.collections.map((col) => (
                      <option key={col} value={col} />
                    ))}
                  </datalist>
                </label>
                <div className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                  Status
                  <div className="mt-1 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleNewProductFieldChange('status', newProductForm.status === 'published' ? 'draft' : 'published')}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                        newProductForm.status === 'published'
                          ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/70'
                          : 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-900/70'
                      }`}
                    >
                      {newProductForm.status === 'published' ? 'Published' : 'Draft'}
                    </button>
                    <span className="text-xs text-brand-gray dark:text-white/60">Click to toggle</span>
                  </div>
                </div>
                <label className="text-sm font-medium text-brand-dark/80 dark:text-white/80 flex items-center gap-2 mt-5">
                  <input
                    type="checkbox"
                    checked={newProductForm.oneOfKind}
                    onChange={(e) => handleNewProductFieldChange('oneOfKind', e.target.checked)}
                    className="h-4 w-4"
                  />
                  One of a kind
                  <InfoTooltip text="Unique, non-serialized piece. The AI pricing agent may suggest a premium markup for one-of-a-kind items based on data analysis." />
                  {newProductForm.oneOfKind && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 tracking-wide uppercase">Unique Piece</span>
                  )}
                </label>
              </div>

              {/* Descriptions */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <label className="text-sm font-medium text-brand-dark/80 dark:text-white/80 block">
                  Description *
                  <textarea
                    value={newProductForm.description}
                    onChange={(e) => handleNewProductFieldChange('description', e.target.value)}
                    placeholder="Product description for customers..."
                    className={`mt-1 w-full px-3 py-2 border rounded-lg min-h-[140px] resize-y ${
                      newProductForm.description.length > 5000
                        ? 'border-red-500 dark:border-red-500'
                        : 'border-black/15 dark:border-white/15'
                    } dark:bg-brand-charcoal/80 dark:text-white`}
                  />
                  <div className={`mt-1 text-xs ${
                    newProductForm.description.length > 5000
                      ? 'text-red-500 font-medium'
                      : 'text-brand-gray dark:text-white/60'
                  }`}>
                    {newProductForm.description.length}/5000 characters
                    {newProductForm.description.length > 5000 && ' - Exceeds limit!'}
                  </div>
                </label>
                <label className="text-sm font-medium text-brand-dark/80 dark:text-white/80 block">
                  SEO Description *
                  <textarea
                    value={newProductForm.seoDescription}
                    onChange={(e) => handleNewProductFieldChange('seoDescription', e.target.value)}
                    placeholder="Search engine optimized description..."
                    className={`mt-1 w-full px-3 py-2 border rounded-lg min-h-[140px] resize-y ${
                      newProductForm.seoDescription.length > 1000
                        ? 'border-red-500 dark:border-red-500'
                        : 'border-black/15 dark:border-white/15'
                    } dark:bg-brand-charcoal/80 dark:text-white`}
                  />
                  <div className={`mt-1 text-xs ${
                    newProductForm.seoDescription.length > 1000
                      ? 'text-red-500 font-medium'
                      : 'text-brand-gray dark:text-white/60'
                  }`}>
                    {newProductForm.seoDescription.length}/1000 characters
                    {newProductForm.seoDescription.length > 1000 && ' - Exceeds limit!'}
                  </div>
                </label>
              </div>

              {/* Variants Builder */}
              <div className="border-t dark:border-white/10 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-brand-dark dark:text-white">Product Variants (Optional)</h3>
                    <p className="text-sm text-brand-gray dark:text-white/60 mt-1">
                      Add variants if this product comes in different colors, materials, or sizes
                    </p>
                  </div>
                  <button
                    onClick={handleAddVariant}
                    className="px-3 py-1.5 bg-brand-blue hover:bg-brand-dark text-white text-sm rounded-lg transition-colors flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Variant
                  </button>
                </div>

                {newVariants.length === 0 ? (
                  <div className="text-center py-8 bg-black/[0.02] dark:bg-white/[0.02] rounded-lg border-2 border-dashed border-black/15 dark:border-white/15">
                    <p className="text-sm text-brand-gray dark:text-white/60">No variants added</p>
                    <p className="text-xs text-brand-gray/60 dark:text-white/40 mt-1">
                      A single inventory record will be created for this product
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {newVariants.map((variant, index) => (
                      <div
                        key={index}
                        className="p-4 border border-black/5 dark:border-white/10 rounded-lg bg-black/[0.02] dark:bg-white/[0.02]"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <span className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                            Variant {index + 1}
                          </span>
                          <button
                            onClick={() => handleRemoveVariant(index)}
                            className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <label className="text-sm text-brand-gray dark:text-white/60">
                            Name *
                            <input
                              value={variant.name}
                              onChange={(e) => handleNewVariantFieldChange(index, 'name', e.target.value)}
                              placeholder="e.g., Gold"
                              className="mt-1 w-full px-2 py-1.5 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded text-sm"
                            />
                          </label>
                          <label className="text-sm text-brand-gray dark:text-white/60">
                            Color
                            <input
                              value={variant.color}
                              onChange={(e) => handleNewVariantFieldChange(index, 'color', e.target.value)}
                              placeholder="e.g., Gold"
                              className="mt-1 w-full px-2 py-1.5 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded text-sm"
                            />
                          </label>
                          <label className="text-sm text-brand-gray dark:text-white/60">
                            Material (optional)
                            <input
                              value={variant.material}
                              onChange={(e) => handleNewVariantFieldChange(index, 'material', e.target.value)}
                              placeholder="Inherits from product"
                              className="mt-1 w-full px-2 py-1.5 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded text-sm"
                            />
                          </label>
                          <label className="text-sm text-brand-gray dark:text-white/60">
                            Price (optional)
                            <input
                              type="number"
                              value={variant.price}
                              onChange={(e) => handleNewVariantFieldChange(index, 'price', e.target.value)}
                              placeholder="Inherits from product"
                              className="mt-1 w-full px-2 py-1.5 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded text-sm"
                            />
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Create Button */}
              <div className="border-t dark:border-white/10 pt-6 mt-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div className="text-sm text-brand-gray dark:text-white/60">
                    After creation, you'll be redirected to add product images.
                  </div>
                  <button
                    onClick={handleCreateProduct}
                    disabled={creatingProduct}
                    className="w-full sm:w-auto px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 inline-flex items-center justify-center gap-2 font-medium text-base shadow-sm hover:shadow-md transition-all"
                  >
                    {creatingProduct ? (
                      <>
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Creating...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Create Product
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Filters and Stats - only shown in list view */}
        {!selectedProduct && !isCreatingNew && (
        <>
        <section className="bg-white/80 dark:bg-brand-charcoal/60 backdrop-blur-md rounded-xl shadow-sm border border-black/5 dark:border-white/10 p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-0 md:min-w-[200px]">
              <label className="text-xs text-brand-gray dark:text-white/60 uppercase tracking-wide mb-1.5 block">Search</label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name, ID, or slug"
                className="w-full px-3 py-2.5 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
              />
            </div>
            <div className="min-w-[140px]">
              <label className="text-xs text-brand-gray dark:text-white/60 uppercase tracking-wide mb-1.5 block">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as 'all' | 'draft' | 'published')}
                className="w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
              >
                <option value="all">All</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            </div>
            <div className="min-w-[140px]">
              <label className="text-xs text-brand-gray dark:text-white/60 uppercase tracking-wide mb-1.5 block">Category</label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
              >
                <option value="all">All</option>
                {filterOptions.categories.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
            <div className="min-w-[140px]">
              <label className="text-xs text-brand-gray dark:text-white/60 uppercase tracking-wide mb-1.5 block">Collection</label>
              <select
                value={filterCollection}
                onChange={(e) => setFilterCollection(e.target.value)}
                className="w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
              >
                <option value="all">All</option>
                {filterOptions.collections.map((collection) => (
                  <option key={collection} value={collection}>{collection}</option>
                ))}
              </select>
            </div>
            <div className="min-w-[140px]">
              <label className="text-xs text-brand-gray dark:text-white/60 uppercase tracking-wide mb-1.5 block">Material</label>
              <select
                value={filterMaterial}
                onChange={(e) => setFilterMaterial(e.target.value)}
                className="w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
              >
                <option value="all">All</option>
                {filterOptions.materials.map((material) => (
                  <option key={material} value={material}>{material}</option>
                ))}
              </select>
            </div>
            <div className="min-w-[140px]">
              <label className="text-xs text-brand-gray dark:text-white/60 uppercase tracking-wide mb-1.5 block">Type</label>
              <select
                value={filterOneOfKind}
                onChange={(e) => setFilterOneOfKind(e.target.value as 'all' | 'true' | 'false')}
                className="w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
              >
                <option value="all">All</option>
                <option value="true">One of a Kind</option>
                <option value="false">Limited Series</option>
              </select>
            </div>
            <div className="min-w-[140px]">
              <label className="text-xs text-brand-gray dark:text-white/60 uppercase tracking-wide mb-1.5 block">Variants</label>
              <select
                value={filterHasVariants}
                onChange={(e) => setFilterHasVariants(e.target.value as 'all' | 'true' | 'false')}
                className="w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
              >
                <option value="all">All</option>
                <option value="true">Has Variants</option>
                <option value="false">No Variants</option>
              </select>
            </div>
            <div className="min-w-[140px]">
              <label className="text-xs text-brand-gray dark:text-white/60 uppercase tracking-wide mb-1.5 block">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                className="w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
              >
                <option value="name">Name</option>
                <option value="newest">Newest</option>
                <option value="price-desc">Price ↓</option>
                <option value="price-asc">Price ↑</option>
                <option value="stock">Stock ↓</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-brand-gray dark:text-white/60 uppercase tracking-wide mb-1.5 block">View</label>
              <div className="flex border border-black/15 dark:border-white/15 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-2 ${viewMode === 'grid' ? 'bg-brand-blue/20 text-brand-blue' : 'text-brand-gray dark:text-white/60 hover:bg-black/5 dark:hover:bg-white/5'}`}
                  title="Grid view"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-2 ${viewMode === 'table' ? 'bg-brand-blue/20 text-brand-blue' : 'text-brand-gray dark:text-white/60 hover:bg-black/5 dark:hover:bg-white/5'}`}
                  title="Table view"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" /></svg>
                </button>
              </div>
            </div>
            <button
              onClick={() => {
                setFilterCategory('all');
                setFilterCollection('all');
                setFilterMaterial('all');
                setFilterStatus('all');
                setFilterOneOfKind('all');
                setFilterHasVariants('all');
                setSortBy('name');
                setSearch('');
              }}
              className="px-4 py-2 border border-black/15 dark:border-white/15 rounded-lg text-brand-dark/80 dark:text-white/80 hover:border-black/25 dark:hover:border-white/25 text-sm"
            >
              Clear
            </button>
          </div>
        </section>
        
        <section className="bg-white/80 dark:bg-brand-charcoal/60 backdrop-blur-md rounded-xl shadow-sm border border-black/5 dark:border-white/10 p-4">
          <div className="flex flex-wrap gap-6 text-sm text-brand-dark/80 dark:text-white/80">
            <div>
              <div className="text-xs text-brand-gray dark:text-white/60 uppercase tracking-wide">Total Products</div>
              <div className="text-lg font-semibold text-brand-dark dark:text-white">{stockStats.totalProducts}</div>
            </div>
            <div>
              <div className="text-xs text-brand-gray dark:text-white/60 uppercase tracking-wide">Products In Stock</div>
              <div className="text-lg font-semibold text-brand-dark dark:text-white">{stockStats.productsInStock}</div>
            </div>
            <div>
              <div className="text-xs text-brand-gray dark:text-white/60 uppercase tracking-wide">Total Units in Stock</div>
              <div className="text-lg font-semibold text-brand-dark dark:text-white">{stockStats.totalUnits}</div>
            </div>
            <div>
              <div className="text-xs text-brand-gray dark:text-white/60 uppercase tracking-wide">Showing</div>
              <div className="text-lg font-semibold text-brand-dark dark:text-white">{sortedProducts.length}</div>
            </div>
          </div>
        </section>
        </>
        )}

        {/* Product Grid View - shown when no product is selected and not creating */}
        {!selectedProduct && !isCreatingNew ? (
          <section className="bg-white/80 dark:bg-brand-charcoal/60 backdrop-blur-md rounded-xl shadow-sm border border-black/5 dark:border-white/10 p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center gap-3 text-brand-gray dark:text-white/60">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Loading products...
                </div>
              </div>
            ) : sortedProducts.length === 0 ? (
              <div className="text-center py-12 text-brand-gray dark:text-white/60">
                No products match your filters.
              </div>
            ) : viewMode === 'table' ? (
              /* Table View */
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-black/10 dark:border-white/10 text-left text-xs text-brand-gray dark:text-white/60 uppercase tracking-wide">
                      <th className="py-2 pr-3 w-10"></th>
                      <th className="py-2 pr-3">Name</th>
                      <th className="py-2 pr-3">Price</th>
                      <th className="py-2 pr-3">Material</th>
                      <th className="py-2 pr-3">Category</th>
                      <th className="py-2 pr-3">Collection</th>
                      <th className="py-2 pr-3">Stock</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedProducts.map((product) => {
                      const fr = formulaMap[product.id];
                      const actual = Number(product.price || product.variants?.[0]?.price || 0);
                      const diff = fr && actual ? Math.round(((actual - fr.recommendedPrice) / fr.recommendedPrice) * 100) : null;
                      return (
                        <tr
                          key={product.id}
                          onClick={() => handleSelect(product)}
                          className="border-b border-black/5 dark:border-white/5 hover:bg-brand-blue/5 dark:hover:bg-brand-blue/10 cursor-pointer transition-colors"
                        >
                          <td className="py-2 pr-3">
                            <div className="w-10 h-10 rounded-lg overflow-hidden bg-black/5 dark:bg-white/5 flex-shrink-0">
                              {resolveImageUrl(product) ? (
                                <img src={resolveImageUrl(product)} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-xs text-brand-gray/40">--</div>
                              )}
                            </div>
                          </td>
                          <td className="py-2 pr-3 font-medium text-brand-dark dark:text-white max-w-[200px] truncate">
                            {product.name || product.id}
                          </td>
                          <td className="py-2 pr-3">
                            <div className="flex items-center gap-1.5">
                              <span className="text-brand-dark dark:text-white">
                                {actual ? `€${actual}` : '—'}
                              </span>
                              {diff !== null && (
                                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                                  diff >= 10 ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400'
                                  : diff >= -10 ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400'
                                  : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400'
                                }`}>
                                  {diff >= 0 ? '+' : ''}{diff}%
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-2 pr-3 text-brand-gray dark:text-white/60 max-w-[120px] truncate">
                            {product.material || '—'}
                          </td>
                          <td className="py-2 pr-3 text-brand-gray dark:text-white/60">
                            {product.category || '—'}
                          </td>
                          <td className="py-2 pr-3 text-brand-gray dark:text-white/60">
                            {product.collection || '—'}
                          </td>
                          <td className="py-2 pr-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              product.inStock
                                ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400'
                                : 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400'
                            }`}>
                              {product.inStock ? product.stockCount || 0 : 'Out'}
                            </span>
                          </td>
                          <td className="py-2 pr-3">
                            {product.status === 'draft' ? (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-400">Draft</span>
                            ) : (
                              <span className="text-xs text-green-600 dark:text-green-400">Live</span>
                            )}
                          </td>
                          <td className="py-2 text-brand-gray dark:text-white/60 text-xs whitespace-nowrap">
                            {product.createdAt ? new Date(product.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              /* Grid View */
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {sortedProducts.map((product) => (
                  <button
                    key={product.id}
                    onClick={() => handleSelect(product)}
                    className="text-left bg-white dark:bg-brand-charcoal/60 rounded-xl border border-black/5 dark:border-white/10 hover:border-brand-blue dark:hover:border-brand-blue hover:shadow-md transition-all overflow-hidden group"
                  >
                    {/* Product Image */}
                    <div className="aspect-square bg-black/5 dark:bg-white/5 overflow-hidden">
                      {resolveImageUrl(product) ? (
                        <img
                          src={resolveImageUrl(product)}
                          alt={product.name || product.id}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-brand-gray/60 dark:text-white/40">
                          <div className="text-center">
                            <div className="text-4xl mb-2">🖼️</div>
                            <div className="text-sm">No image</div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="font-semibold text-brand-dark dark:text-white line-clamp-2">
                          {product.name || product.id}
                        </h3>
                        {product.status === 'draft' && (
                          <span className="flex-shrink-0 px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-400 rounded text-xs font-medium">
                            Draft
                          </span>
                        )}
                      </div>

                      <div className="text-sm text-brand-gray dark:text-white/60 mb-2">
                        {product.category && <span>{product.category}</span>}
                        {product.category && product.collection && <span> • </span>}
                        {product.collection && <span>{product.collection}</span>}
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium text-brand-dark dark:text-white">
                            €{product.price || product.variants?.[0]?.price || '—'}
                          </span>
                          {(() => {
                            const fr = formulaMap[product.id];
                            if (!fr) return null;
                            const actual = Number(product.price || product.variants?.[0]?.price || 0);
                            if (!actual) return null;
                            const diff = Math.round(((actual - fr.recommendedPrice) / fr.recommendedPrice) * 100);
                            const isAbove = diff >= 10;
                            const isClose = diff >= -10 && diff < 10;
                            const isBelow = diff < -10;
                            return (
                              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                                isAbove ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400'
                                : isClose ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400'
                                : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400'
                              }`}>
                                {diff >= 0 ? '+' : ''}{diff}%
                              </span>
                            );
                          })()}
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          product.inStock
                            ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400'
                            : 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400'
                        }`}>
                          {product.inStock ? `${product.stockCount || 0} in stock` : 'Out of stock'}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>
        ) : selectedProduct ? (
          /* Edit View - shown when a product is selected */
          <section className="bg-white/80 dark:bg-brand-charcoal/60 backdrop-blur-md rounded-xl shadow-sm border border-black/5 dark:border-white/10 p-6">
            <div className="space-y-6">
              {/* Back Button - Prominent */}
              <button
                onClick={() => {
                  setSelectedId(null);
                  // Update URL without the product hash
                  window.history.pushState({}, '', window.location.pathname);
                }}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 text-brand-dark/80 dark:text-white/90 rounded-lg transition-all group border border-black/5 dark:border-white/10"
              >
                <svg className="w-5 h-5 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span className="font-semibold">Back to All Products</span>
              </button>

              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-semibold text-brand-dark dark:text-white">{selectedProduct.name}</h2>
                  {pendingChanges.length > 0 && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 rounded-full text-xs font-medium">
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                      Modified
                    </span>
                  )}
                  {selectedProduct.slug && (
                    <div className="flex items-center gap-1">
                      <a
                        href={`https://camila-parente-staging-store.vercel.app/product/${selectedProduct.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 dark:bg-blue-900/50 hover:bg-blue-200 dark:hover:bg-blue-900/70 text-blue-700 dark:text-blue-300 rounded-l-lg text-xs font-medium transition-colors"
                        title="View on staging site"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Staging
                      </a>
                      <a
                        href={`https://www.camilaparente.store/product/${selectedProduct.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 dark:bg-green-900/50 hover:bg-green-200 dark:hover:bg-green-900/70 text-green-700 dark:text-green-300 rounded-r-lg text-xs font-medium transition-colors"
                        title="View on production site"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Production
                      </a>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-3 text-sm text-brand-gray dark:text-white/60">
                  <span>SKU: {selectedProduct.id}</span>
                  <span className="text-black/20 dark:text-white/20">|</span>
                  <span>Stock: {selectedProduct.stockCount ?? 0}</span>
                  <span className={
                    selectedProduct.inStock
                      ? 'text-green-600 dark:text-green-500'
                      : selectedProduct.madeToOrder
                        ? 'text-amber-600 dark:text-amber-500'
                        : 'text-red-500 dark:text-red-400'
                  }>
                    {selectedProduct.inStock
                      ? 'In Stock'
                      : selectedProduct.madeToOrder
                        ? 'Made to Order'
                        : 'Out of Stock'}
                  </span>
                  <span className="text-black/20 dark:text-white/20">|</span>
                  <button
                    onClick={() => { setDeleteConfirmName(''); setShowDeleteConfirm(true); }}
                    className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:underline"
                  >
                    Delete Product
                  </button>
                </div>
              </div>

              {/* Toast notification rendered at bottom of viewport */}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                  Name
                  <input
                    value={form.name}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
                  />
                </label>
                <label className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                  Slug
                  <input
                    value={form.slug}
                    onChange={(e) => handleFieldChange('slug', e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
                  />
                </label>
                <div className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                  Price (€)
                  <input
                    value={form.price}
                    onChange={(e) => handleFieldChange('price', e.target.value)}
                    placeholder={form.hasVariants ? 'Set per variant below' : ''}
                    className="mt-1 w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
                  />
                  {editFormulaResult && (
                    <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-brand-gray dark:text-white/50">
                        Formula: €{editFormulaResult.recommendedPrice}
                      </span>
                      <InfoTooltip text={
                        `€${editFormulaResult.breakdown.materialComponent.toFixed(1)} material (cost × ${FORMULA_PARAMS.MATERIAL_MARGIN}) `
                        + `+ €${editFormulaResult.breakdown.craftBase} craft base `
                        + `= €${editFormulaResult.breakdown.baseValue.toFixed(1)} `
                        + `× ${editFormulaResult.breakdown.complexityMultiplier.toFixed(2)} complexity (${editFormulaResult.breakdown.complexity}/10) `
                        + `× ${editFormulaResult.breakdown.overheadMultiplier} overhead `
                        + `= €${editFormulaResult.recommendedPrice}`
                      } />
                      {(() => {
                        const p = Number(form.price);
                        const f = editFormulaResult.recommendedPrice;
                        const diff = f > 0 ? Math.round(((p - f) / f) * 100) : null;
                        return form.price && diff !== null && isFinite(diff) ? (
                          <span className={`text-xs font-medium ${
                            p >= f ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'
                          }`}>
                            {p >= f ? '+' : ''}{diff}% vs formula
                          </span>
                        ) : null;
                      })()}
                      <button
                        type="button"
                        onClick={() => handleFieldChange('price', String(editFormulaResult!.recommendedPrice))}
                        className="text-xs text-brand-blue hover:underline"
                      >
                        Apply
                      </button>
                    </div>
                  )}
                </div>
                <div className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                  Production Cost (€) <InfoTooltip text="Auto-calculated from weight × cost/gram when both are filled. You can override manually for edge cases." />
                  {editComputedCost !== null && !manualCostOverride ? (
                    <>
                      <div className="mt-1 w-full px-3 py-2 bg-gray-50 dark:bg-brand-charcoal/40 border border-black/10 dark:border-white/10 rounded-lg text-brand-dark dark:text-white">
                        €{editComputedCost.toFixed(2)} <span className="text-xs text-brand-gray dark:text-white/50">(auto)</span>
                      </div>
                      <button type="button" onClick={() => setManualCostOverride(true)} className="mt-1 text-xs text-brand-blue hover:underline">Override</button>
                    </>
                  ) : (
                    <>
                      <input
                        value={form.basePrice}
                        onChange={(e) => handleFieldChange('basePrice', e.target.value)}
                        className="mt-1 w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
                      />
                      {manualCostOverride && editComputedCost !== null && (
                        <button type="button" onClick={() => setManualCostOverride(false)} className="mt-1 text-xs text-brand-blue hover:underline">Use auto (€{editComputedCost.toFixed(2)})</button>
                      )}
                    </>
                  )}
                </div>
                <label className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                  Weight (grams)
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="e.g. 12.5"
                    value={form.weightGrams}
                    onChange={(e) => handleFieldChange('weightGrams', e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
                  />
                </label>
                <label className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                  Material Cost/gram (€)
                  <input
                    type="number"
                    step="0.0001"
                    min="0"
                    placeholder="e.g. 0.0650"
                    value={form.materialCostPerGram}
                    onChange={(e) => handleFieldChange('materialCostPerGram', e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
                  />
                </label>
                <label className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                  Complexity (1–10) <InfoTooltip text="How intricate is this piece to make? 1 = simple, 10 = extremely detailed. Higher complexity means more labor and should command a higher price." />
                  <input
                    type="number"
                    min="1"
                    max="10"
                    step="1"
                    placeholder="1–10"
                    value={form.complexity}
                    onChange={(e) => handleFieldChange('complexity', e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
                  />
                </label>
                <label className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                  Material
                  <input
                    value={form.material}
                    onChange={(e) => handleFieldChange('material', e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
                  />
                </label>
                <label className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                  Category
                  <input
                    value={form.category}
                    onChange={(e) => handleFieldChange('category', e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
                  />
                  {form.category === 'Rings' && (
                    <span className="block mt-1 text-xs text-amber-600 dark:text-amber-400">Ring sizes & per-size inventory managed in code (ProductInfo.tsx)</span>
                  )}
                </label>
                <label className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                  Collection
                  <div className="mt-1 flex gap-2">
                    <select
                      value={filterOptions.collections.includes(form.collection) ? form.collection : '__new__'}
                      onChange={(e) => {
                        if (e.target.value === '__new__') {
                          handleFieldChange('collection', '');
                        } else {
                          handleFieldChange('collection', e.target.value);
                        }
                      }}
                      className="flex-1 px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
                    >
                      {filterOptions.collections.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                      <option value="__new__">+ New collection...</option>
                    </select>
                  </div>
                  {(!filterOptions.collections.includes(form.collection)) && (
                    <input
                      value={form.collection}
                      onChange={(e) => handleFieldChange('collection', e.target.value)}
                      placeholder="Enter new collection name"
                      className="mt-2 w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
                      autoFocus
                    />
                  )}
                </label>
                <div className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                  Status
                  <div className="mt-1 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleFieldChange('status', form.status === 'published' ? 'draft' : 'published')}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                        form.status === 'published'
                          ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/70'
                          : 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-900/70'
                      }`}
                    >
                      {form.status === 'published' ? (
                        <>
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Published
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          Draft
                        </>
                      )}
                    </button>
                    <span className="text-xs text-brand-gray dark:text-white/60">
                      {form.status === 'published' ? 'Visible on storefront' : 'Hidden from customers'} • Click to toggle
                    </span>
                  </div>
                </div>
                <div className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                  Date Created
                  <input
                    type="date"
                    value={form.createdAt}
                    onChange={(e) => handleFieldChange('createdAt', e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg"
                  />
                  {form.createdAt && (
                    <div className="mt-1 text-xs text-brand-gray dark:text-white/50">
                      {new Date(form.createdAt + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </div>
                  )}
                </div>
                <label className="text-sm font-medium text-brand-dark/80 dark:text-white/80 flex items-center gap-2 mt-5">
                  <input
                    type="checkbox"
                    checked={form.oneOfKind}
                    onChange={(e) => handleFieldChange('oneOfKind', e.target.checked)}
                    className="h-4 w-4"
                  />
                  One of a kind
                  <InfoTooltip text="Unique, non-serialized piece. The AI pricing agent may suggest a premium markup for one-of-a-kind items based on data analysis." />
                  {form.oneOfKind && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 tracking-wide uppercase">Unique Piece</span>
                  )}
                </label>
              </div>

              {/* Descriptions Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <label className="text-sm font-medium text-brand-dark/80 dark:text-white/80 block">
                  Description
                  <textarea
                    value={form.description}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    placeholder="Product description for customers..."
                    className={`mt-1 w-full px-3 py-2 border rounded-lg min-h-[140px] resize-y ${
                      form.description.length > 5000
                        ? 'border-red-500 dark:border-red-500'
                        : 'border-black/15 dark:border-white/15'
                    } dark:bg-brand-charcoal/80 dark:text-white`}
                  />
                  <div className={`mt-1 text-xs ${
                    form.description.length > 5000
                      ? 'text-red-500 font-medium'
                      : 'text-brand-gray dark:text-white/60'
                  }`}>
                    {form.description.length}/5000 characters
                    {form.description.length > 5000 && ' - Exceeds limit!'}
                  </div>
                </label>

                <label className="text-sm font-medium text-brand-dark/80 dark:text-white/80 block">
                  SEO Description
                  <textarea
                    value={form.seoDescription}
                    onChange={(e) => handleFieldChange('seoDescription', e.target.value)}
                    placeholder="Search engine optimized description..."
                    className={`mt-1 w-full px-3 py-2 border rounded-lg min-h-[140px] resize-y ${
                      form.seoDescription.length > 1000
                        ? 'border-red-500 dark:border-red-500'
                        : 'border-black/15 dark:border-white/15'
                    } dark:bg-brand-charcoal/80 dark:text-white`}
                  />
                  <div className={`mt-1 text-xs ${
                    form.seoDescription.length > 1000
                      ? 'text-red-500 font-medium'
                      : 'text-brand-gray dark:text-white/60'
                  }`}>
                    {form.seoDescription.length}/1000 characters
                    {form.seoDescription.length > 1000 && ' - Exceeds limit!'}
                  </div>
                </label>
              </div>

              {/* Inventory Management Section */}
              <div className="border-t dark:border-white/10 pt-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-brand-dark dark:text-white">Inventory</h3>
                    <p className="text-sm text-brand-gray dark:text-white/60 mt-1">
                      Manage stock levels and availability options
                    </p>
                  </div>
                </div>

                {/* Products without variants - show direct stock management */}
                {selectedProduct && !selectedProduct.hasVariants && (
                  <div className="bg-black/[0.02] dark:bg-white/[0.02] rounded-lg p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {/* Stock Status Badge */}
                        <span className={`px-2.5 py-1 rounded-full text-sm font-medium ${
                          (selectedProduct.stockCount || 0) > 0
                            ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400'
                            : selectedProduct.madeToOrder
                              ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400'
                              : 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400'
                        }`}>
                          {(selectedProduct.stockCount || 0) > 0
                            ? `${selectedProduct.stockCount} in stock`
                            : selectedProduct.madeToOrder
                              ? 'Made to Order'
                              : 'Out of Stock'}
                        </span>
                      </div>

                      {/* Stock Edit Controls */}
                      <div className="flex items-center gap-3">
                        {inventoryEditing[selectedProduct.id] ? (
                          <>
                            <input
                              type="number"
                              min="0"
                              value={inventoryEditing[selectedProduct.id].stockCount}
                              onChange={(e) => updateInventoryValue(selectedProduct.id, Math.max(0, parseInt(e.target.value) || 0))}
                              className="w-20 px-2 py-1.5 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded text-center text-sm"
                              disabled={inventoryEditing[selectedProduct.id].saving}
                            />
                            {inventoryEditing[selectedProduct.id].success ? (
                              <span className="text-green-600 dark:text-green-500 text-sm flex items-center gap-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Saved
                              </span>
                            ) : (
                              <>
                                <button
                                  onClick={() => saveInventoryStock(selectedProduct.id, selectedProduct.inventoryRecordId || selectedProduct.id)}
                                  disabled={inventoryEditing[selectedProduct.id].saving}
                                  className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-sm disabled:opacity-50"
                                >
                                  {inventoryEditing[selectedProduct.id].saving ? '...' : 'Save'}
                                </button>
                                <button
                                  onClick={() => cancelInventoryEditing(selectedProduct.id)}
                                  disabled={inventoryEditing[selectedProduct.id].saving}
                                  className="px-3 py-1.5 border border-black/15 dark:border-white/15 text-brand-dark/80 dark:text-white/80 rounded text-sm hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50"
                                >
                                  Cancel
                                </button>
                              </>
                            )}
                            {inventoryEditing[selectedProduct.id].error && (
                              <span className="text-red-600 dark:text-red-400 text-sm">{inventoryEditing[selectedProduct.id].error}</span>
                            )}
                          </>
                        ) : (
                          <button
                            onClick={() => startInventoryEditing(selectedProduct.id, selectedProduct.stockCount || 0)}
                            className="px-3 py-1.5 bg-brand-blue/10 dark:bg-brand-blue/10 text-brand-blue dark:text-brand-blue rounded text-sm hover:bg-brand-blue/20 dark:hover:bg-brand-blue/20 transition-colors"
                          >
                            Edit Stock
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Made to Order Toggle */}
                    <div className="flex items-center justify-between pt-3 border-t border-black/5 dark:border-white/10">
                      <div>
                        <label className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                          Made to Order
                        </label>
                        <p className="text-xs text-brand-gray dark:text-white/60 mt-0.5">
                          Allow purchases when out of stock (1-2 week production)
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleMadeToOrder(selectedProduct.id, selectedProduct.inventoryRecordId || selectedProduct.id, selectedProduct.madeToOrder || false)}
                          disabled={madeToOrderState[selectedProduct.id]?.saving}
                          className={`relative w-11 h-6 rounded-full transition-colors ${
                            selectedProduct.madeToOrder
                              ? 'bg-amber-500'
                              : 'bg-black/20 dark:bg-white/20'
                          } ${madeToOrderState[selectedProduct.id]?.saving ? 'opacity-50' : ''}`}
                        >
                          <span
                            className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                              selectedProduct.madeToOrder ? 'translate-x-5' : ''
                            }`}
                          />
                        </button>
                        {madeToOrderState[selectedProduct.id]?.error && (
                          <span className="text-red-500 text-xs">{madeToOrderState[selectedProduct.id].error}</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Products with variants - show variant-level stock management */}
                {selectedProduct && selectedProduct.hasVariants && editedVariants.length > 0 && (
                  <div className="bg-black/[0.02] dark:bg-white/[0.02] rounded-lg overflow-hidden">
                    <div className="p-3 bg-black/5 dark:bg-white/5/50 border-b border-black/5 dark:border-white/10">
                      <p className="text-sm text-brand-gray dark:text-white/60">
                        This product has variants. Manage stock for each variant below.
                      </p>
                    </div>
                    <div className="divide-y divide-black/5 dark:divide-white/10">
                      {editedVariants.map((variant: any) => {
                        const variantEditing = inventoryEditing[variant.id];
                        const variantStock = variant.stockCount || 0;
                        const variantMadeToOrder = variant.madeToOrder || false;

                        return (
                          <div key={variant.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                            {/* Variant Info */}
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-brand-dark dark:text-white/90 truncate">
                                {variant.name || variant.id}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  variantStock > 0
                                    ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400'
                                    : variantMadeToOrder
                                      ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400'
                                      : 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400'
                                }`}>
                                  {variantStock > 0 ? `${variantStock} in stock` : variantMadeToOrder ? 'Made to Order' : 'Out of Stock'}
                                </span>
                              </div>
                            </div>

                            {/* Made to Order Toggle */}
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-brand-gray dark:text-white/60">MTO</span>
                              <button
                                onClick={() => toggleMadeToOrder(variant.id, variant.id, variantMadeToOrder)}
                                disabled={madeToOrderState[variant.id]?.saving}
                                className={`relative w-9 h-5 rounded-full transition-colors ${
                                  variantMadeToOrder
                                    ? 'bg-amber-500'
                                    : 'bg-black/20 dark:bg-white/20'
                                } ${madeToOrderState[variant.id]?.saving ? 'opacity-50' : ''}`}
                              >
                                <span
                                  className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                                    variantMadeToOrder ? 'translate-x-4' : ''
                                  }`}
                                />
                              </button>
                            </div>

                            {/* Stock Edit */}
                            <div className="flex items-center gap-2">
                              {variantEditing ? (
                                <>
                                  <input
                                    type="number"
                                    min="0"
                                    value={variantEditing.stockCount}
                                    onChange={(e) => updateInventoryValue(variant.id, Math.max(0, parseInt(e.target.value) || 0))}
                                    className="w-16 px-2 py-1 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded text-center text-sm"
                                    disabled={variantEditing.saving}
                                  />
                                  {variantEditing.success ? (
                                    <span className="text-green-600 dark:text-green-500 text-xs flex items-center gap-1">
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                      Saved
                                    </span>
                                  ) : (
                                    <>
                                      <button
                                        onClick={() => saveInventoryStock(variant.id, variant.id)}
                                        disabled={variantEditing.saving}
                                        className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs disabled:opacity-50"
                                      >
                                        {variantEditing.saving ? '...' : 'Save'}
                                      </button>
                                      <button
                                        onClick={() => cancelInventoryEditing(variant.id)}
                                        disabled={variantEditing.saving}
                                        className="px-2 py-1 border border-black/15 dark:border-white/15 text-brand-gray dark:text-white/60 rounded text-xs hover:bg-black/5 dark:hover:bg-white/5"
                                      >
                                        ✕
                                      </button>
                                    </>
                                  )}
                                </>
                              ) : (
                                <button
                                  onClick={() => startInventoryEditing(variant.id, variantStock)}
                                  className="px-2 py-1 bg-brand-blue/10 dark:bg-brand-blue/10 text-brand-blue dark:text-brand-blue rounded text-xs hover:bg-brand-blue/20 dark:hover:bg-brand-blue/20 transition-colors"
                                >
                                  Edit
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-brand-dark dark:text-white">Product Media</h3>
                  <p className="text-sm text-brand-gray dark:text-white/60 mt-1">
                    Media uploads are saved immediately to staging.
                  </p>
                </div>

                {/* Product Images Upload */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {(['product-main', 'product-secondary', 'product-third'] as const).map((usage) => {
                    const label = usage.replace('product-', '').replace('-', ' ');
                    const labelCap = label.charAt(0).toUpperCase() + label.slice(1);
                    const productMedia = selectedProduct ? getMediaByProduct(selectedProduct.id || selectedProduct.id) : [];

                    // Find media with EXACT usage match (not just includes)
                    const currentMedia = productMedia.find(m => {
                      // Check if this specific usage is in the array
                      return Array.isArray(m.usage) && m.usage.includes(usage as any);
                    });
                    // Use the matched media's URL directly, not through pickProductMediaUrl fallback
                    const currentMediaUrl = currentMedia?.cloudinaryURL || undefined;

                    // Debug logging for image slots
                    if (import.meta.env.DEV && selectedProduct) {
                      console.debug(`[CMS Images] ${usage}:`, {
                        mediaFound: !!currentMedia,
                        url: currentMediaUrl ? currentMediaUrl.slice(-30) : 'none',
                        totalProductMedia: productMedia.length,
                        mediaUsages: productMedia.map(m => ({ id: m.id, usage: m.usage }))
                      });
                    }
                    const isUploading = uploadingMedia[usage];
                    const isDeleting = deletingMedia[usage];
                    const error = uploadErrors[usage];
                    const pending = pendingMedia[usage];
                    const pendingPreview = pending ? URL.createObjectURL(pending) : null;

                    return (
                      <div key={usage} className="border-2 border-dashed border-black/15 dark:border-white/15 rounded-lg p-4 hover:border-brand-blue dark:hover:border-brand-blue transition bg-white dark:bg-brand-charcoal/60">
                        <div className="text-sm font-medium text-brand-dark/80 dark:text-white/80 mb-2">{labelCap} Image</div>

                        {/* Current Media Preview */}
                        {currentMediaUrl && !isUploading && !pending ? (
                          <div className="mb-3 relative group">
                            <div className="rounded-lg overflow-hidden bg-black/5 dark:bg-white/5 border dark:border-white/15">
                              {currentMedia?.mediaType === 'video' ? (
                                <video src={currentMediaUrl} className="w-full h-40 object-cover" muted playsInline preload="metadata" />
                              ) : (
                                <img src={currentMediaUrl} alt={labelCap} className="w-full h-40 object-cover" />
                              )}
                            </div>
                            {currentMedia?.cloudinaryPublicId && (
                              <div className="text-[10px] font-mono text-brand-gray dark:text-white/50 truncate mt-1" title={currentMedia.cloudinaryPublicId}>
                                {currentMedia.cloudinaryPublicId.split('/').pop()}
                              </div>
                            )}
                            {currentMedia && (
                              <button
                                onClick={() => setConfirmDeleteUsage(usage)}
                                disabled={isDeleting}
                                className="absolute top-2 right-2 bg-brand-blue hover:bg-brand-dark text-white px-2 py-1 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50 text-xs font-medium"
                                title="Unlink media from this product"
                              >
                                🔗 Unlink
                              </button>
                            )}
                          </div>
                        ) : !isUploading && !pending && (
                          <div className="mb-3">
                            <div className="rounded-lg bg-black/[0.02] dark:bg-brand-dark/50 border-2 border-dashed border-black/15 dark:border-white/15 h-40 flex items-center justify-center">
                              <div className="text-center text-brand-gray/60 dark:text-white/40">
                                <div className="text-3xl mb-2">🖼️</div>
                                <div className="text-sm">No image linked</div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Pending Upload Preview */}
                        {pending && pendingPreview && (
                          <div className="mb-3">
                            <div className="rounded-lg overflow-hidden bg-black/5 dark:bg-white/5 border border-brand-blue">
                              {pending.type.startsWith('video/') ? (
                                <video src={pendingPreview} className="w-full h-40 object-cover" controls />
                              ) : (
                                <img src={pendingPreview} alt="Preview" className="w-full h-40 object-cover" />
                              )}
                            </div>
                            <div className="mt-2 flex gap-2">
                              <button
                                onClick={() => handleMediaUpload(pending, usage)}
                                disabled={isUploading}
                                className="flex-1 bg-brand-blue text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-50"
                              >
                                ✓ Confirm Upload
                              </button>
                              <button
                                onClick={() => {
                                  setPendingMedia(prev => ({ ...prev, [usage]: null }));
                                  if (pendingPreview) URL.revokeObjectURL(pendingPreview);
                                }}
                                disabled={isUploading}
                                className="px-3 py-2 border border-black/15 dark:border-white/15 rounded-lg text-sm text-brand-dark/80 dark:text-white/80 hover:bg-black/[0.02] dark:hover:bg-white/5 disabled:opacity-50"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Upload Area */}
                        {!pending && (
                          <>
                            <div className="relative">
                              <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/heic,image/heif,video/mp4,video/webm,video/quicktime"
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                disabled={isUploading}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    // Validate file size
                                    const maxSize = file.type.startsWith('video/') ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
                                    if (file.size > maxSize) {
                                      setUploadErrors(prev => ({
                                        ...prev,
                                        [usage]: `File too large. Max ${file.type.startsWith('video/') ? '100MB' : '10MB'}.`
                                      }));
                                      return;
                                    }
                                    // Store pending file for preview
                                    setPendingMedia(prev => ({ ...prev, [usage]: file }));
                                    setUploadErrors(prev => ({ ...prev, [usage]: null }));
                                  }
                                  e.target.value = ''; // Reset input
                                }}
                              />
                              <div className={`border-2 border-dashed rounded-lg p-4 text-center ${
                                isUploading ? 'bg-black/[0.02] dark:bg-brand-dark/50 border-black/15 dark:border-white/15' : 'hover:bg-black/[0.02] dark:hover:bg-brand-dark/50 border-black/15 dark:border-white/15 cursor-pointer'
                              }`}>
                                {isUploading ? (
                                  <div className="flex items-center justify-center gap-2 text-sm text-brand-gray dark:text-white/60">
                                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Uploading...
                                  </div>
                                ) : (
                                  <>
                                    <div className="text-2xl mb-2">📷</div>
                                    <div className="text-sm text-brand-gray dark:text-white/60">
                                      {currentMediaUrl ? 'Replace Media' : 'Click or drag media'}
                                    </div>
                                    <div className="text-xs text-brand-gray/60 dark:text-white/40 mt-1">
                                      Images: JPG, PNG, WebP, HEIC (max 10MB)
                                      <br />
                                      Videos: MP4, WebM, MOV (max 100MB)
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Browse Existing Button */}
                            <div className="mt-2">
                              <button
                                onClick={() => setShowMediaBrowser(usage)}
                                disabled={isUploading}
                                className="w-full px-3 py-2 text-sm border border-brand-blue dark:border-brand-blue text-brand-blue dark:text-brand-blue rounded-lg hover:bg-brand-blue/5 dark:hover:bg-brand-blue/10 disabled:opacity-50 transition"
                              >
                                📁 Browse Existing Media
                              </button>
                            </div>
                          </>
                        )}

                        {/* Error Message */}
                        {error && (
                          <div className="mt-2 text-xs text-red-600 flex items-start gap-1">
                            <span>⚠️</span>
                            <span>{error}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Extra Media Management */}
                <div className="border-t dark:border-white/10 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="text-md font-medium text-brand-dark dark:text-white/90">Extra Media</h4>
                      <p className="text-xs text-brand-gray dark:text-white/60 mt-1">
                        Additional images and videos for this product (reusable across products)
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <label className={`px-3 py-1.5 ${uploadingExtraMedia ? 'bg-brand-gray cursor-wait' : 'bg-green-600 hover:bg-green-700 cursor-pointer'} text-white text-sm rounded-lg transition-colors inline-flex items-center gap-1`}>
                        {uploadingExtraMedia ? 'Uploading...' : '+ Upload New'}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,video/mp4,video/webm,video/quicktime"
                          className="hidden"
                          disabled={uploadingExtraMedia}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleExtraMediaUpload(file);
                            e.target.value = '';
                          }}
                        />
                      </label>
                      <button
                        onClick={() => setShowExtraMediaBrowser(true)}
                        className="px-3 py-1.5 bg-brand-blue hover:bg-brand-dark text-white text-sm rounded-lg transition-colors"
                      >
                        + Browse Existing
                      </button>
                    </div>
                  </div>

                  {(() => {
                    const allProductMedia = getMediaByProduct(selectedProduct?.id || '');
                    // Only exclude the specific media items displayed in the 3 slots above
                    // (not ALL media with slot usages — a product-main from another product linked as extra should still show)
                    const displayedSlotIds = new Set<string>();
                    for (const usage of ['product-main', 'product-secondary', 'product-third'] as const) {
                      const found = allProductMedia.find(m => Array.isArray(m.usage) && m.usage.includes(usage));
                      if (found) displayedSlotIds.add(found.id);
                    }
                    // Also exclude variant images (they have their own section)
                    for (const m of allProductMedia) {
                      if (Array.isArray(m.usage) && m.usage.includes('variant-image')) displayedSlotIds.add(m.id);
                    }
                    // Extra media = everything linked but not currently displayed in a named slot
                    const extraMedia = allProductMedia.filter(
                      m => !displayedSlotIds.has(m.id) && !recentlyUnlinkedMedia.has(m.id)
                    );

                    return extraMedia.length === 0 ? (
                      <div className="text-center py-8 bg-black/[0.02] dark:bg-white/[0.02] rounded-lg border-2 border-dashed border-black/15 dark:border-white/15">
                        <p className="text-sm text-brand-gray dark:text-white/60 mb-3">No extra media attached</p>
                        <div className="flex items-center justify-center gap-3">
                          <label className={`text-sm ${uploadingExtraMedia ? 'text-brand-gray cursor-wait' : 'text-green-600 hover:text-green-700 cursor-pointer'} underline`}>
                            {uploadingExtraMedia ? 'Uploading...' : 'Upload new media'}
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp,image/heic,image/heif,video/mp4,video/webm,video/quicktime"
                              className="hidden"
                              disabled={uploadingExtraMedia}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleExtraMediaUpload(file);
                                e.target.value = '';
                              }}
                            />
                          </label>
                          <span className="text-brand-gray dark:text-white/40">or</span>
                          <button
                            onClick={() => setShowExtraMediaBrowser(true)}
                            className="text-sm text-brand-blue dark:text-brand-blue hover:text-brand-dark dark:hover:text-brand-blue underline"
                          >
                            Browse existing media
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {extraMedia.map((mediaItem) => (
                          <div key={mediaItem.id} className="rounded-lg overflow-hidden border dark:border-white/10 bg-white dark:bg-brand-charcoal/60 relative group">
                            {/* Preview */}
                            <div className="aspect-square bg-black/5 dark:bg-white/5">
                              {mediaItem.mediaType === 'image' ? (
                                <img
                                  src={mediaItem.cloudinaryURL}
                                  alt={mediaItem.id}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <video
                                  src={mediaItem.cloudinaryURL}
                                  className="w-full h-full object-cover"
                                  muted
                                  playsInline
                                  preload="metadata"
                                />
                              )}
                            </div>

                            {/* Info */}
                            <div className="px-2 py-1.5">
                              <div className="text-[10px] font-mono text-brand-gray dark:text-white/60 truncate" title={mediaItem.cloudinaryPublicId || mediaItem.id}>
                                {(mediaItem.cloudinaryPublicId || mediaItem.id).split('/').pop()}
                              </div>
                              <div className="text-[10px] text-brand-gray/60 dark:text-white/40 mt-0.5">
                                {mediaItem.mediaType}
                                {mediaItem.products && mediaItem.products.length > 1 && (
                                  <span className="ml-1">• Shared ({mediaItem.products.length} products)</span>
                                )}
                              </div>
                            </div>

                            {/* Unlink Button */}
                            <button
                              onClick={() => handleUnlinkExtraMedia(mediaItem.id)}
                              disabled={unlinkingExtraMedia === mediaItem.id}
                              className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                              title="Unlink from this product"
                            >
                              {unlinkingExtraMedia === mediaItem.id ? '...' : '✕'}
                            </button>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Variants Section */}
              {selectedProduct && editedVariants.length > 0 && (
                <div className="space-y-4">
                  <div className="border-t dark:border-white/10 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-brand-dark dark:text-white flex items-center gap-2">
                          Product Variants ({editedVariants.length})
                          {variantsModified && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 rounded text-xs font-medium">
                              Modified
                            </span>
                          )}
                        </h3>
                        <p className="text-sm text-brand-gray dark:text-white/60 mt-1">
                          Click on a variant to edit its images, price, and material
                        </p>
                      </div>
                    </div>

                    {variantImageError && (
                      <div className="mb-3 p-3 bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-900 rounded-lg text-red-700 dark:text-red-300 text-sm">
                        {variantImageError}
                      </div>
                    )}

                    <div className="space-y-4">
                      {editedVariants.map((variant: any, index: number) => {
                        const isExpanded = expandedVariantIndex === index;
                        const variantImages = variant.images
                          ? (Array.isArray(variant.images) ? variant.images : Object.values(variant.images))
                          : [];
                        const mainImage = variantImages[0] || null;

                        return (
                          <div
                            key={variant.id || index}
                            className={`border rounded-lg overflow-hidden bg-white dark:bg-brand-charcoal/60 transition-all ${
                              isExpanded
                                ? 'border-brand-blue dark:border-brand-blue'
                                : 'border-black/5 dark:border-white/10'
                            }`}
                          >
                            {/* Variant Header */}
                            <div className="flex items-center gap-4 p-3">
                              {/* Thumbnail */}
                              <div className="w-16 h-16 rounded-lg bg-black/5 dark:bg-white/5 flex-shrink-0 overflow-hidden">
                                {mainImage ? (
                                  <img src={mainImage} alt={variant.name || variant.id} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-brand-gray/60 dark:text-white/40 text-xl">
                                    🖼️
                                  </div>
                                )}
                              </div>

                              {/* Info */}
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-brand-dark dark:text-white truncate">
                                  {variant.name || variant.id}
                                </div>
                                <div className="text-sm text-brand-gray dark:text-white/60 flex items-center gap-3">
                                  <span>${variant.price || selectedProduct.price || '—'}</span>
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    variant.inStock
                                      ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400'
                                      : 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400'
                                  }`}>
                                    {variant.stockCount || 0} in stock
                                  </span>
                                  {variant.color && <span>Color: {variant.color}</span>}
                                </div>
                              </div>

                              {/* Edit Button - Clearly visible */}
                              <button
                                onClick={() => setExpandedVariantIndex(isExpanded ? null : index)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                                  isExpanded
                                    ? 'bg-brand-blue text-white hover:bg-brand-dark'
                                    : 'bg-brand-blue/10 dark:bg-brand-blue/10 text-brand-blue dark:text-brand-blue hover:bg-brand-blue/20 dark:hover:bg-brand-blue/20'
                                }`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                {isExpanded ? 'Close' : 'Edit'}
                              </button>
                            </div>

                            {/* Expanded Content */}
                            {isExpanded && (
                              <div className="border-t dark:border-white/10 p-4 space-y-4 bg-black/[0.02] dark:bg-brand-dark/50">
                                {/* Variant Images Grid */}
                                <div>
                                  <h4 className="text-sm font-medium text-brand-dark/80 dark:text-white/80 mb-2">Variant Images</h4>
                                  <div className="grid grid-cols-3 gap-3">
                                    {['Main', 'Model', 'Detail'].map((label, slot) => {
                                      const imageUrl = variantImages[slot] || null;
                                      const isUploading = variantImageUploading?.variantIndex === index && variantImageUploading?.slot === slot;

                                      return (
                                        <div key={slot} className="border-2 border-dashed border-black/15 dark:border-white/15 rounded-lg p-2 hover:border-brand-blue dark:hover:border-brand-blue transition bg-white dark:bg-brand-charcoal/60">
                                          <div className="text-xs font-medium text-brand-gray dark:text-white/60 mb-2">{label}</div>

                                          {/* Image Preview */}
                                          <div className="aspect-square rounded-lg overflow-hidden bg-black/5 dark:bg-white/5 mb-2">
                                            {imageUrl ? (
                                              <img src={imageUrl} alt={`${label}`} className="w-full h-full object-cover" />
                                            ) : (
                                              <div className="w-full h-full flex items-center justify-center text-brand-gray/60 dark:text-white/40">
                                                <div className="text-center">
                                                  <div className="text-2xl mb-1">🖼️</div>
                                                  <div className="text-[10px]">No image</div>
                                                </div>
                                              </div>
                                            )}
                                          </div>

                                          {/* Action Buttons */}
                                          <div className="flex gap-1">
                                            {/* Upload Button */}
                                            <div className="relative flex-1">
                                              <input
                                                type="file"
                                                accept="image/jpeg,image/png,image/webp,image/heic,image/heif,video/mp4,video/webm,video/quicktime"
                                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                                disabled={isUploading}
                                                onChange={(e) => {
                                                  const file = e.target.files?.[0];
                                                  if (file) {
                                                    handleVariantImageUpload(file, index, slot);
                                                  }
                                                  e.target.value = '';
                                                }}
                                              />
                                              <div className={`text-center py-2 px-1 rounded text-[10px] border transition ${
                                                isUploading
                                                  ? 'bg-black/5 dark:bg-white/5 border-black/15 dark:border-white/15 text-brand-gray'
                                                  : 'bg-brand-blue/5 dark:bg-brand-blue/10 border-brand-blue/30 dark:border-brand-blue/30 text-brand-blue dark:text-brand-blue cursor-pointer hover:bg-brand-blue/10 dark:hover:bg-brand-blue/20'
                                              }`}>
                                                {isUploading ? (
                                                  <span className="flex items-center justify-center gap-1">
                                                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                    </svg>
                                                  </span>
                                                ) : (
                                                  '📷 Upload'
                                                )}
                                              </div>
                                            </div>
                                            {/* Browse Library Button */}
                                            <button
                                              type="button"
                                              onClick={() => setShowVariantMediaBrowser({ variantIndex: index, slot })}
                                              disabled={isUploading}
                                              className="flex-1 text-center py-2 px-1 rounded text-[10px] border transition bg-brand-blue/5 dark:bg-brand-blue/10 border-brand-blue/30 dark:border-brand-blue/30 text-brand-blue dark:text-brand-blue hover:bg-brand-blue/10 dark:hover:bg-brand-blue/20 disabled:opacity-50"
                                            >
                                              🔍 Browse
                                            </button>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>

                                {/* Variant Details */}
                                <div className="grid grid-cols-2 gap-4">
                                  <div className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                                    Price
                                    <input
                                      type="number"
                                      value={variant.price || ''}
                                      onChange={(e) => handleVariantFieldChange(index, 'price', e.target.value ? Number(e.target.value) : '')}
                                      placeholder={`${selectedProduct.price || 'Parent price'}`}
                                      className="mt-1 w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg text-sm"
                                    />
                                    {/* Variant formula recommendation */}
                                    {(() => {
                                      const variantMaterial = variant.material || form.material;
                                      if (!editComputedCost || !variantMaterial) return null;
                                      const result = calculateRecommendedPrice({
                                        productionCost: editComputedCost,
                                        material: variantMaterial,
                                        complexity: form.complexity ? Number(form.complexity) : null,
                                        oneOfKind: form.oneOfKind,
                                      });
                                      const vPrice = Number(variant.price);
                                      const diff = result.recommendedPrice > 0 ? Math.round(((vPrice - result.recommendedPrice) / result.recommendedPrice) * 100) : null;
                                      return (
                                        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                                          <span className="text-xs text-brand-gray dark:text-white/50">
                                            Formula: €{result.recommendedPrice}
                                          </span>
                                          <InfoTooltip text={
                                            `€${result.breakdown.materialComponent.toFixed(1)} material `
                                            + `+ €${result.breakdown.craftBase} craft `
                                            + `= €${result.breakdown.baseValue.toFixed(1)} `
                                            + `× ${result.breakdown.complexityMultiplier.toFixed(2)} complexity `
                                            + `× ${result.breakdown.overheadMultiplier} overhead `
                                            + `= €${result.recommendedPrice}`
                                          } />
                                          {variant.price && diff !== null && isFinite(diff) ? (
                                            <span className={`text-xs font-medium ${
                                              vPrice >= result.recommendedPrice ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'
                                            }`}>
                                              {vPrice >= result.recommendedPrice ? '+' : ''}{diff}% vs formula
                                            </span>
                                          ) : null}
                                          <button
                                            type="button"
                                            onClick={() => handleVariantFieldChange(index, 'price', result.recommendedPrice)}
                                            className="text-xs text-brand-blue hover:underline"
                                          >
                                            Apply
                                          </button>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                  <label className="text-sm font-medium text-brand-dark/80 dark:text-white/80">
                                    Material
                                    <input
                                      type="text"
                                      value={variant.material || ''}
                                      onChange={(e) => handleVariantFieldChange(index, 'material', e.target.value)}
                                      placeholder={form.material || 'Parent material'}
                                      className="mt-1 w-full px-3 py-2 border border-black/15 dark:border-white/15 dark:bg-brand-charcoal/80 dark:text-white rounded-lg text-sm"
                                    />
                                  </label>
                                </div>

                                {/* Variant ID */}
                                <div className="text-[10px] font-mono text-brand-gray/60 dark:text-white/40">
                                  ID: {variant.id}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Product Details Save Section */}
              <div className="border-t dark:border-white/10 pt-6 mt-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div className="text-sm text-brand-gray dark:text-white/60">
                    Media uploads are saved immediately. Product details require Save.
                  </div>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="w-full sm:w-auto px-6 py-3 bg-brand-blue text-white rounded-lg hover:bg-brand-dark disabled:opacity-50 inline-flex items-center justify-center gap-2 font-medium text-base shadow-sm hover:shadow-md transition-all"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          </section>
        ) : null}
      </main>

      {showConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-brand-charcoal rounded-xl shadow-xl max-w-xl w-full p-6">
            <h3 className="text-lg font-semibold text-brand-dark dark:text-white">Confirm changes</h3>
            <p className="text-sm text-brand-gray dark:text-white/60 mt-1">
              Review the changes below before saving.
            </p>
            <div className="mt-4 max-h-64 overflow-y-auto border dark:border-white/10 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-black/[0.02] dark:bg-white/[0.03] text-brand-gray dark:text-white/60">
                  <tr>
                    <th className="text-left px-3 py-2">Field</th>
                    <th className="text-left px-3 py-2">From</th>
                    <th className="text-left px-3 py-2">To</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingChanges.map((change) => (
                    <tr key={change.field} className="border-t dark:border-white/10">
                      <td className="px-3 py-2 font-medium text-brand-dark dark:text-white/90">{change.field}</td>
                      <td className="px-3 py-2 text-brand-gray dark:text-white/60">{change.from}</td>
                      <td className="px-3 py-2 text-brand-dark dark:text-white">{change.to}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 border border-black/15 dark:border-white/15 rounded-lg text-brand-dark/80 dark:text-white/80 hover:bg-black/[0.02] dark:hover:bg-white/[0.03]"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSave}
                disabled={saving}
                className="px-4 py-2 bg-brand-blue dark:bg-brand-blue text-white rounded-lg hover:bg-brand-dark dark:hover:bg-brand-dark disabled:opacity-60"
              >
                {saving ? 'Saving...' : 'Confirm & Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteUsage && (() => {
        const productMedia = selectedProduct ? getMediaByProduct(selectedProduct.id || selectedProduct.id) : [];
        const currentMedia = productMedia.find(m => {
          // usage is always an array in MediaContext
          return m.usage.includes(confirmDeleteUsage as any);
        });

        if (!currentMedia) return null;

        return (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-brand-charcoal rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-brand-dark dark:text-white flex items-center gap-2">
                <svg className="w-5 h-5 text-brand-blue dark:text-brand-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                Unlink Media
              </h3>
              <p className="text-sm text-brand-gray dark:text-white/60 mt-2">
                This will unlink the {confirmDeleteUsage.replace('product-', '')} media from this product. The media will remain in Cloudinary and can be reused with other products.
              </p>
              <div className="mt-4 p-3 bg-black/[0.02] dark:bg-white/[0.03] rounded-lg border dark:border-white/10">
                <div className="text-xs text-brand-gray dark:text-white/60 mb-1">Media ID</div>
                <div className="text-sm font-mono text-brand-dark dark:text-white">{currentMedia.id}</div>
              </div>
              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  onClick={() => setConfirmDeleteUsage(null)}
                  className="px-4 py-2 border border-black/15 dark:border-white/15 rounded-lg text-brand-dark/80 dark:text-white/80 hover:bg-black/[0.02] dark:hover:bg-white/[0.03]"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (currentMedia.id && currentMedia.cloudinaryPublicId) {
                      handleMediaDelete(confirmDeleteUsage, currentMedia.id, currentMedia.cloudinaryPublicId);
                    }
                  }}
                  disabled={deletingMedia[confirmDeleteUsage]}
                  className="px-4 py-2 bg-brand-blue text-white rounded-lg hover:bg-brand-dark disabled:opacity-60"
                >
                  {deletingMedia[confirmDeleteUsage] ? 'Unlinking...' : 'Unlink Media'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Extra Media Browser Modal */}
      <ExtraMediaBrowser
        isOpen={showExtraMediaBrowser}
        onClose={() => setShowExtraMediaBrowser(false)}
        onSelectMedia={handleLinkExtraMedia}
        currentProductCategory={selectedProduct?.category}
        currentProductId={selectedProduct?.id}
        usageFilter="all"
        title="Media Library"
        description="Select media to add as extra images for this product"
      />

      {/* Main/Secondary/Third Media Browser Modal */}
      {showMediaBrowser && (
        <ExtraMediaBrowser
          isOpen={true}
          onClose={() => setShowMediaBrowser(null)}
          onSelectMedia={handleLinkMedia}
          currentProductCategory={selectedProduct?.category}
          currentProductId={selectedProduct?.id}
          usageFilter="all"
          title="Media Library"
          description={`Select media to use as ${showMediaBrowser.replace('product-', '')} image`}
        />
      )}

      {/* Variant Media Browser Modal */}
      {showVariantMediaBrowser && (
        <ExtraMediaBrowser
          isOpen={true}
          onClose={() => setShowVariantMediaBrowser(null)}
          onSelectMedia={handleVariantMediaSelect}
          currentProductCategory={selectedProduct?.category}
          currentProductId={selectedProduct?.id}
          usageFilter="all"
          title="Select Variant Image"
          description={`Select image for variant ${['Main', 'Model', 'Detail'][showVariantMediaBrowser.slot] || 'image'} slot`}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedProduct && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-brand-charcoal rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-brand-dark dark:text-white flex items-center gap-2">
              <svg className="w-5 h-5 text-red-600 dark:text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Delete Product
            </h3>
            <p className="text-sm text-brand-gray dark:text-white/60 mt-3">
              Are you sure you want to delete <strong className="text-brand-dark dark:text-white">{selectedProduct.name || selectedProduct.id}</strong>?
            </p>
            <p className="text-sm text-red-600 dark:text-red-400 mt-2">
              This action cannot be undone. The product and all its inventory records will be permanently removed from staging.
            </p>
            <div className="mt-4 p-3 bg-black/[0.02] dark:bg-white/[0.03] rounded-lg border dark:border-white/10">
              <div className="text-xs text-brand-gray dark:text-white/60 mb-1">Product ID</div>
              <div className="text-sm font-mono text-brand-dark dark:text-white">{selectedProduct.id}</div>
              {selectedProduct.variants && selectedProduct.variants.length > 0 && (
                <div className="mt-2 text-xs text-brand-gray dark:text-white/60">
                  + {selectedProduct.variants.length} variant{selectedProduct.variants.length !== 1 ? 's' : ''} will also be deleted
                </div>
              )}
            </div>
            <div className="mt-4">
              <label className="block text-sm text-brand-gray dark:text-white/60 mb-1.5">
                Type <strong className="text-brand-dark dark:text-white">{selectedProduct.name || selectedProduct.id}</strong> to confirm:
              </label>
              <input
                type="text"
                autoFocus
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                placeholder={selectedProduct.name || selectedProduct.id}
                className="w-full px-3 py-2.5 rounded-lg border border-black/15 dark:border-white/15 bg-white dark:bg-brand-dark text-brand-dark dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-red-500/40"
              />
            </div>
            <div className="mt-5 flex items-center justify-end gap-3">
              <button
                onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmName(''); }}
                disabled={deletingProduct}
                className="px-4 py-2.5 border border-black/15 dark:border-white/15 rounded-lg text-brand-dark/80 dark:text-white/80 hover:bg-black/[0.02] dark:hover:bg-white/[0.03] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteProduct}
                disabled={deletingProduct || deleteConfirmName !== (selectedProduct.name || selectedProduct.id)}
                className="px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 transition-opacity"
              >
                {deletingProduct ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Deleting...
                  </>
                ) : (
                  'Delete Product'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fixed-position toast — always visible regardless of scroll */}
      {(error || success) && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-md w-[calc(100%-2rem)]">
          <div
            className={`px-4 py-3 rounded-lg shadow-lg border text-sm flex items-center gap-2 ${
              error
                ? 'bg-red-50 dark:bg-red-950 border-red-300 dark:border-red-800 text-red-700 dark:text-red-300'
                : 'bg-green-50 dark:bg-green-950 border-green-300 dark:border-green-800 text-green-700 dark:text-green-300'
            }`}
          >
            <span className="flex-1">{error || success}</span>
            <button
              onClick={() => { setError(null); setSuccess(null); }}
              className="ml-2 opacity-60 hover:opacity-100 text-lg leading-none"
            >
              &times;
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
